import './App.css';
import './css-fixes.css';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation, useNavigate } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './LanguageContext';
import { getTranslation } from './translations';
import LanguageSelector from './LanguageSelector';
import { validateToken, clearAuthAndRedirect, handleAuthError } from './authUtils';
import Register from './Register';
import Login from './Login';
import EmailVerification from './EmailVerification';
import RegistrationSuccess from './RegistrationSuccess';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import PasswordResetSuccess from './PasswordResetSuccess';
import FriendlyGame from './FriendlyGame';
import FriendlyGameMenu from './FriendlyGameMenu';
import FriendlyGamesReview from './FriendlyGamesReview';
import FriendlyGameStats from './FriendlyGameStats';
import TournamentMenu from './TournamentMenu';
import AddTournament from './AddTournament';
import TournamentReview from './TournamentReview';
import TournamentMatches from './TournamentMatches';
import TournamentStats from './TournamentStats';
import TournamentMatchStats from './TournamentMatchStats';
import TournamentDetails from './TournamentDetails';
import FinishedTournamentDetails from './FinishedTournamentDetails';
import AddTournamentRound from './AddTournamentRound';
import FinishTournament from './FinishTournament';
import ReactCalendar from 'react-calendar';
//import 'react-calendar/dist/Calendar.css';
import { StaticDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import pl from 'date-fns/locale/pl';
import fr from 'date-fns/locale/fr';
import enUS from 'date-fns/locale/en-US';
import { DateCalendar } from '@mui/x-date-pickers';
import { PickersDay } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTitle, Tooltip, Legend);

// Separate component for back button
const BackButton = () => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(-1)}
      style={{
        background: 'none',
        border: 'none',
        fontSize: 24,
        cursor: 'pointer',
        color: '#888',
        padding: 8,
        borderRadius: 4,
        transition: 'color 0.2s',
        textDecoration: 'none',
      }}
      title="Wróć"
    >
      ←
    </button>
  );
};

// Breadcrumbs component
const PATH_LABELS = {
  '': 'menu',
  'trening': 'trening',
  'strzal-w-kule': 'strzał w kulę',
  'strzal-w-swinke': 'strzał w świnkę',
  'puenta-wysoka': 'puenta wysoka',
  'puenta-niska': 'puenta niska',
  'przeglad': 'przegląd treningów',
  'statystyki': 'statystyki treningów',
  'mecz': 'mecz',
  'turniej-rankingowy': 'turniej rankingowy',
  'dodaj': 'dodaj turniej',
  'szczegoly': 'szczegóły turnieju',
  'dodaj-runde': 'dodaj rundę',
  'zakoncz': 'zakończ turniej',
  'mecze': 'przegląd meczów turniejowych',
  'mecz-towarzyski': 'mecz towarzyski',
  'ustawienia': 'ustawienia',
  'usun-konto': 'usuwanie konta',
  'rejestracja': 'rejestracja',
  'logowanie': 'logowanie',
};

function Breadcrumbs() {
  const { language } = useLanguage();
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);
  
  // Special cases: skip the intermediate 'mecz' segment for specific routes
  let crumbs;
  if (location.pathname === '/mecz/mecz-towarzyski') {
    crumbs = ['', 'mecz-towarzyski'];
  } else if (location.pathname === '/mecz/turniej-rankingowy') {
    crumbs = ['', 'turniej-rankingowy'];
  } else {
    crumbs = [''].concat(segments);
  }
  
  return (
    <div style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, fontSize: window.innerWidth <= 600 ? 18 : 36, color: '#1e3a8a', textAlign: 'center', letterSpacing: 1 }}>
      {crumbs.map((seg, idx) => {
        const decodedSeg = decodeURIComponent(seg);
        return (
          <span key={idx}>
            {getTranslation(decodedSeg, language) || decodedSeg}
            {idx < crumbs.length - 1 && ' / '}
          </span>
        );
      })}
    </div>
  );
}



// Main content component that can use useNavigate
function AppContent() {
  const { language } = useLanguage();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [trainings, setTrainings] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const navigate = useNavigate();

  // Validate token on component mount (page load/refresh)
  useEffect(() => {
    const checkTokenValidity = async () => {
      const token = sessionStorage.getItem('access_token');
      
      if (!token) {
        setIsLoggedIn(false);
        setIsValidatingToken(false);
        return;
      }

      try {
        const isValid = await validateToken();
        if (isValid) {
          setIsLoggedIn(true);
        } else {
          // Token is invalid/expired, clear it and redirect
          clearAuthAndRedirect();
          return;
        }
      } catch (error) {
        console.error('Token validation error:', error);
        // On error, assume token is invalid for security
        clearAuthAndRedirect();
        return;
      } finally {
        setIsValidatingToken(false);
      }
    };

    checkTokenValidity();
  }, []);

  useEffect(() => {
    // Fetch trainings only if logged in and token validation is complete
    if (isLoggedIn && !isValidatingToken) {
      const token = sessionStorage.getItem('access_token');
      fetch('/api/trainings', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => {
          if (res.status === 401) {
            handleAuthError(res);
            return [];
          }
          return res.ok ? res.json() : [];
        })
        .then(data => setTrainings(data))
        .catch(() => setTrainings([]));
    }
  }, [isLoggedIn, isValidatingToken]);

  useEffect(() => {
    // Fetch user profile only if logged in and token validation is complete
    if (isLoggedIn && !isValidatingToken) {
      const token = sessionStorage.getItem('access_token');
      fetch('/api/user/profile/with-club', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => {
          if (res.status === 401) {
            handleAuthError(res);
            return null;
          }
          return res.ok ? res.json() : null;
        })
        .then(data => setUserProfile(data))
        .catch(() => setUserProfile(null));
    }
  }, [isLoggedIn, isValidatingToken]);

  const handleLogout = () => {
    sessionStorage.removeItem('access_token');
    setIsLoggedIn(false);
    window.location.href = '/';
  };

  // Function to refresh trainings data (shared across components)
  const refreshTrainings = () => {
    const token = sessionStorage.getItem('access_token');
    if (token) {
      fetch('/api/trainings', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      .then(res => {
        if (res.status === 401) {
          handleAuthError(res);
          return [];
        }
        return res.ok ? res.json() : [];
      })
      .then(data => setTrainings(data))
      .catch(() => setTrainings([]));
    }
  };

  const squareButtonStyle = {
    width: 'auto',
    height: 'auto',
    background: 'none',
    color: '#888',
    fontWeight: 700,
    fontSize: 22,
    letterSpacing: 1,
    margin: 18,
    border: 'none',
    boxShadow: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s',
    textAlign: 'center',
    textDecoration: 'none',
    textTransform: 'lowercase',
    fontFamily: 'Nunito, Arial, sans-serif',
    padding: 0,
    borderBottom: '4px solid transparent',
    display: 'inline-block',
  };

  // Helper to detect mobile
  const isMobile = window.innerWidth <= 600;

  // Show loading spinner while validating token
  if (isValidatingToken) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#f8f8f8',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '20px', color: '#666', fontFamily: 'Nunito, Arial, sans-serif' }}>
          {getTranslation('validatingSession', language) || 'Validating session...'}
        </p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f8f8f8'
    }}>
        <Routes>
          <Route path="/register" element={<Register />} />
          <Route path="/registration-success" element={<RegistrationSuccess />} />
          <Route path="/login" element={<Login onLogin={() => setIsLoggedIn(true)} />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/password-reset-success" element={<PasswordResetSuccess />} />
          <Route path="/*" element={
            <div style={{ 
              minHeight: '100vh', 
              background: '#f0f8ff'
            }}>

            <Routes>
              <Route path="/" element={
                isLoggedIn ? (
                  <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
                    {/* Language Selector - Top Right */}
                    <div style={{
                      position: 'absolute',
                      top: '20px',
                      right: '20px',
                      zIndex: 1000
                    }}>
                      <LanguageSelector />
                    </div>

                    {/* Main Title */}
                    <h1 style={{
                      fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
                      fontWeight: 'bold',
                      color: '#7c3aed',
                      margin: '0 0 16px 0',
                      textAlign: 'center',
                      fontFamily: 'Nunito, Arial, sans-serif',
                      padding: window.innerWidth <= 600 ? '0 20px' : '0'
                    }}>
                      {getTranslation('shootMe', language)}
                    </h1>

                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                      <div style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, fontSize: window.innerWidth <= 600 ? 18 : 36, color: '#1e3a8a', margin: '24px 0', letterSpacing: 1 }}>
                        {getTranslation('mainMenu', language)}
                      </div>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: window.innerWidth <= 600 ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
                        gap: window.innerWidth <= 600 ? '16px' : '24px', 
                        width: '100%', 
                        maxWidth: '800px',
                        padding: window.innerWidth <= 600 ? '0 20px' : '0'
                      }}>
                        <Link to="/trening" style={{
                          background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
                          color: '#4a148c',
                          textDecoration: 'none',
                          padding: '24px 20px',
                          borderRadius: '16px',
                          fontWeight: '700',
                          fontSize: '1.1rem',
                          textAlign: 'center',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          transition: 'transform 0.3s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <span style={{ fontSize: '2rem' }}>🎯</span>
                          {getTranslation('training', language)}
                        </Link>
                        <Link to="/mecz/mecz-towarzyski" style={{
                          background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
                          color: '#1b5e20',
                          textDecoration: 'none',
                          padding: '24px 20px',
                          borderRadius: '16px',
                          fontWeight: '700',
                          fontSize: '1.1rem',
                          textAlign: 'center',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          transition: 'transform 0.3s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <span style={{ fontSize: '2rem' }}>🤝</span>
                          {getTranslation('friendlyGame', language)}
                        </Link>
                        <Link to="/mecz/turniej-rankingowy" style={{
                          background: 'linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%)',
                          color: '#1e3a8a',
                          textDecoration: 'none',
                          padding: '24px 20px',
                          borderRadius: '16px',
                          fontWeight: '700',
                          fontSize: '1.1rem',
                          textAlign: 'center',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          transition: 'transform 0.3s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <span style={{ fontSize: '2rem' }}>🏆</span>
                          {getTranslation('tournament', language)}
                        </Link>
                        <Link to="/ustawienia" style={{
                          background: 'linear-gradient(135deg, #fff3e0 0%, #ffcc80 100%)',
                          color: '#e65100',
                          textDecoration: 'none',
                          padding: '24px 20px',
                          borderRadius: '16px',
                          fontWeight: '700',
                          fontSize: '1.1rem',
                          textAlign: 'center',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          transition: 'transform 0.3s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <span style={{ fontSize: '2rem' }}>⚙️</span>
                          {getTranslation('settings', language)}
                        </Link>
                        <button
                          onClick={handleLogout}
                          style={{
                            background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                            color: '#c62828',
                            textDecoration: 'none',
                            padding: '24px 20px',
                            borderRadius: '16px',
                            fontWeight: '700',
                            fontSize: '1.1rem',
                            textAlign: 'center',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            transition: 'transform 0.3s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '12px',
                            border: 'none',
                            cursor: 'pointer',
                            fontFamily: 'Nunito, Arial, sans-serif'
                          }}
                        >
                          <span style={{ fontSize: '2rem' }}>🚪</span>
                          {getTranslation('logout', language)}
                        </button>
                        <button
                          onClick={() => navigate('/usun-konto')}
                          style={{
                            background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)',
                            color: '#ad1457',
                            textDecoration: 'none',
                            padding: '24px 20px',
                            borderRadius: '16px',
                            fontWeight: '700',
                            fontSize: '1.1rem',
                            textAlign: 'center',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            transition: 'transform 0.3s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '12px',
                            border: 'none',
                            cursor: 'pointer',
                            fontFamily: 'Nunito, Arial, sans-serif'
                          }}
                        >
                          <span style={{ fontSize: '2rem' }}>🗑️</span>
                          {getTranslation('deleteAccount', language)}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    minHeight: '100vh',
                    background: '#f0f8ff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '40px 0',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    position: 'relative'
                  }}>
                    {/* Language Selector - Top Right */}
                    <div style={{
                      position: 'absolute',
                      top: '20px',
                      right: '20px',
                      zIndex: 1000
                    }}>
                      <LanguageSelector />
                    </div>

                    {/* Main Title */}
                    <h1 style={{
                      fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
                      fontWeight: 'bold',
                      color: '#7c3aed',
                      margin: '0 0 16px 0',
                      textAlign: 'center'
                    }}>
                      {getTranslation('shootMe', language)}
                    </h1>
                    
                    {/* Subtitle */}
                    <p style={{
                      fontSize: '1.2rem',
                      color: '#6b7280',
                      margin: '0 auto 48px auto',
                      textAlign: 'center',
                      maxWidth: '600px',
                      lineHeight: '1.6'
                    }}>
                      {getTranslation('appDescription', language)}
                    </p>
                    
                    {/* Login/Register Buttons */}
                    <div style={{
                      display: 'flex',
                      gap: '24px',
                      marginBottom: '80px',
                      flexWrap: 'wrap',
                      justifyContent: 'center'
                    }}>
                      <Link to="/login" style={{
                        padding: window.innerWidth <= 600 ? '14px 28px' : '16px 32px',
                        backgroundColor: '#7c3aed',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '12px',
                        fontWeight: '600',
                        fontSize: window.innerWidth <= 600 ? '1rem' : '1.1rem',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)'
                      }}>
                        {getTranslation('loginButton', language)}
                      </Link>
                      <Link to="/register" style={{
                        padding: window.innerWidth <= 600 ? '14px 28px' : '16px 32px',
                        backgroundColor: 'white',
                        color: '#7c3aed',
                        textDecoration: 'none',
                        borderRadius: '12px',
                        fontWeight: '600',
                        fontSize: window.innerWidth <= 600 ? '1rem' : '1.1rem',
                        border: '2px solid #7c3aed',
                        transition: 'all 0.3s ease'
                      }}>
                        {getTranslation('registerButton', language)}
                      </Link>
                    </div>
                    
                    {/* Feature Cards Grid - Responsive Layout */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: window.innerWidth <= 600 ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: window.innerWidth <= 600 ? '20px' : '32px',
                      maxWidth: window.innerWidth <= 600 ? '90%' : '1200px',
                      width: '100%',
                      padding: window.innerWidth <= 600 ? '0 20px' : '0'
                    }}>
                      {/* Card 1: Graj. Zapisuj. Rozwijaj się. */}
                      <div style={{
                        background: 'linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%)',
                        borderRadius: '20px',
                        padding: '32px 24px',
                        textAlign: 'center',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.3s ease'
                      }}>
                        <div style={{
                          width: '60px',
                          height: '60px',
                          backgroundColor: '#0288d1',
                          borderRadius: '50%',
                          margin: '0 auto 20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <span style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>🎯</span>
                        </div>
                        <h3 style={{
                          fontSize: '1.3rem',
                          fontWeight: '700',
                          color: '#1e3a8a',
                          margin: '0 0 12px 0'
                        }}>
                          {getTranslation('card1Title', language)}
                        </h3>
                        <p style={{
                          fontSize: '1rem',
                          color: '#475569',
                          margin: '0',
                          lineHeight: '1.5'
                        }}>
                          {getTranslation('card1Description', language)}
                        </p>
                      </div>
                      
                      {/* Card 2: Opanuj celność */}
                      <div style={{
                        background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
                        borderRadius: '20px',
                        padding: '32px 24px',
                        textAlign: 'center',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.3s ease'
                      }}>
                        <div style={{
                          width: '60px',
                          height: '60px',
                          backgroundColor: '#4caf50',
                          borderRadius: '50%',
                          margin: '0 auto 20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <span style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>🎯</span>
                        </div>
                        <h3 style={{
                          fontSize: '1.3rem',
                          fontWeight: '700',
                          color: '#1b5e20',
                          margin: '0 0 12px 0'
                        }}>
                          {getTranslation('card2Title', language)}
                        </h3>
                        <p style={{
                          fontSize: '1rem',
                          color: '#2e7d32',
                          margin: '0',
                          lineHeight: '1.5'
                        }}>
                          {getTranslation('card2Description', language)}
                        </p>
                      </div>
                      
                      {/* Card 3: Czas na grę! */}
                      <div style={{
                        background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
                        borderRadius: '20px',
                        padding: '32px 24px',
                        textAlign: 'center',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.3s ease'
                      }}>
                        <div style={{
                          width: '60px',
                          height: '60px',
                          backgroundColor: '#9c27b0',
                          borderRadius: '50%',
                          margin: '0 auto 20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <span style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>🏆</span>
                        </div>
                        <h3 style={{
                          fontSize: '1.3rem',
                          fontWeight: '700',
                          color: '#4a148c',
                          margin: '0 0 12px 0'
                        }}>
                          {getTranslation('card3Title', language)}
                        </h3>
                        <p style={{
                          fontSize: '1rem',
                          color: '#6a1b9a',
                          margin: '0',
                          lineHeight: '1.5'
                        }}>
                          {getTranslation('card3Description', language)}
                        </p>
                      </div>
                      
                      {/* Card 4: Twoje statystyki, twoja historia */}
                      <div style={{
                        background: 'linear-gradient(135deg, #fff3e0 0%, #ffcc80 100%)',
                        borderRadius: '20px',
                        padding: '32px 24px',
                        textAlign: 'center',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.3s ease'
                      }}>
                        <div style={{
                          width: '60px',
                          height: '60px',
                          backgroundColor: '#ff9800',
                          borderRadius: '8px',
                          margin: '0 auto 20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <span style={{ color: 'white', fontSize: '20px', fontWeight: 'bold' }}>📊</span>
                        </div>
                        <h3 style={{
                          fontSize: '1.3rem',
                          fontWeight: '700',
                          color: '#e65100',
                          margin: '0 0 12px 0'
                        }}>
                          {getTranslation('card4Title', language)}
                        </h3>
                        <p style={{
                          fontSize: '1rem',
                          color: '#f57c00',
                          margin: '0',
                          lineHeight: '1.5'
                        }}>
                          {getTranslation('card4Description', language)}
                        </p>
                      </div>
                      
                      {/* Card 5: Wejdź na wyższy poziom */}
                      <div style={{
                        background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)',
                        borderRadius: '20px',
                        padding: '32px 24px',
                        textAlign: 'center',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.3s ease'
                      }}>
                        <div style={{
                          width: '60px',
                          height: '60px',
                          backgroundColor: '#e91e63',
                          borderRadius: '50%',
                          margin: '0 auto 20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <span style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>🚀</span>
                        </div>
                        <h3 style={{
                          fontSize: '1.3rem',
                          fontWeight: '700',
                          color: '#880e4f',
                          margin: '0 0 12px 0'
                        }}>
                          {getTranslation('card5Title', language)}
                        </h3>
                        <p style={{
                          fontSize: '1rem',
                          color: '#ad1457',
                          margin: '0',
                          lineHeight: '1.5'
                        }}>
                          {getTranslation('card5Description', language)}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              } />
              <Route path="/mecz" element={
                <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
                  {/* Main Title */}
                  <h1 style={{
                    fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
                    fontWeight: 'bold',
                    color: '#7c3aed',
                    margin: '0 0 16px 0',
                    textAlign: 'center',
                    fontFamily: 'Nunito, Arial, sans-serif'
                  }}>
                    ShootMe
                  </h1>
                  {/* Back button and breadcrumbs in horizontal layout */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '20px',
                    margin: '24px 0'
                  }}>
                    <BackButton />
                    <Breadcrumbs />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: window.innerWidth <= 600 ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
                      gap: window.innerWidth <= 600 ? '16px' : '24px', 
                      width: '100%', 
                      maxWidth: '800px',
                      padding: window.innerWidth <= 600 ? '0 20px' : '0'
                    }}>
                    <Link to="/mecz/turniej-rankingowy" style={{
                      background: 'linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%)',
                      color: '#1e3aed',
                      textDecoration: 'none',
                      padding: '24px 20px',
                      borderRadius: '16px',
                      fontWeight: '700',
                      fontSize: '1.1rem',
                      textAlign: 'center',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      transition: 'transform 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <span style={{ fontSize: '2rem' }}>🏆</span>
                      turniej rankingowy
                    </Link>
                    <Link to="/mecz/mecz-towarzyski" style={{
                      background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
                      color: '#1b5e20',
                      textDecoration: 'none',
                      padding: '24px 20px',
                      borderRadius: '16px',
                      fontWeight: '700',
                      fontSize: '1.1rem',
                      textAlign: 'center',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          transition: 'transform 0.3s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <span style={{ fontSize: '2rem' }}>🤝</span>
                          mecz towarzyski
                        </Link>
                      </div>
                    </div>
                  </div>
                } />
                <Route path="/mecz/turniej-rankingowy" element={<TournamentMenu />} />
                <Route path="/mecz/turniej-rankingowy/dodaj" element={<AddTournament />} />
                <Route path="/mecz/turniej-rankingowy/szczegoly/:tournamentId" element={<TournamentDetails />} />
                <Route path="/mecz/turniej-rankingowy/szczegoly/:tournamentId/dodaj-runde" element={<AddTournamentRound />} />
                <Route path="/mecz/turniej-rankingowy/szczegoly/:tournamentId/zakoncz" element={<FinishTournament />} />
                <Route path="/mecz/turniej-rankingowy/przeglad" element={<TournamentReview />} />
                <Route path="/mecz/turniej-rankingowy/przeglad/:tournamentId/rundy" element={<FinishedTournamentDetails />} />
                <Route path="/mecz/turniej-rankingowy/mecze" element={<TournamentMatches />} />
                <Route path="/mecz/turniej-rankingowy/statystyki-turniejow" element={<TournamentStats />} />
                <Route path="/mecz/turniej-rankingowy/statystyki-meczow" element={<TournamentMatchStats />} />

                <Route path="/mecz/mecz-towarzyski" element={<FriendlyGameMenu />} />
                <Route path="/mecz/mecz-towarzyski/dodaj" element={<FriendlyGame userProfile={userProfile} />} />
                <Route path="/mecz/mecz-towarzyski/przeglad" element={<FriendlyGamesReview />} />
                <Route path="/mecz/mecz-towarzyski/statystyki" element={<FriendlyGameStats />} />
                <Route path="/trening" element={
                  <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
                    {/* Language Selector - Top Right */}
                    <div style={{
                      position: 'absolute',
                      top: '20px',
                      right: '20px',
                      zIndex: 1000
                    }}>
                      <LanguageSelector />
                    </div>

                    {/* Main Title */}
                    <h1 style={{
                      fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
                      fontWeight: 'bold',
                      color: '#7c3aed',
                      margin: '0 0 16px 0',
                      textAlign: 'center',
                      fontFamily: 'Nunito, Arial, sans-serif'
                    }}>
                      {getTranslation('shootMe', language)}
                    </h1>
                    {/* Back button and breadcrumbs in horizontal layout */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      gap: '20px',
                      margin: '24px 0'
                    }}>
                      <BackButton />
                      <Breadcrumbs />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: window.innerWidth <= 600 ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
                        gap: window.innerWidth <= 600 ? '16px' : '24px', 
                        width: '100%', 
                        maxWidth: '800px',
                        padding: window.innerWidth <= 600 ? '0 20px' : '0'
                      }}>
                      <Link to="/trening/strzal-w-kule" style={{
                        background: 'linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%)',
                        color: '#1e3a8a',
                        textDecoration: 'none',
                        padding: '24px 20px',
                        borderRadius: '16px',
                        fontWeight: '700',
                        fontSize: '1.1rem',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{ fontSize: '2rem' }}>🎯</span>
                        {getTranslation('shotAtBall', language)}
                      </Link>
                      <Link to="/trening/strzal-w-swinke" style={{
                        background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
                        color: '#1b5e20',
                        textDecoration: 'none',
                        padding: '24px 20px',
                        borderRadius: '16px',
                        fontWeight: '700',
                        fontSize: '1.1rem',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{ fontSize: '2rem' }}>🐷</span>
                        {getTranslation('shotAtPig', language)}
                      </Link>
                      <Link to="/trening/puenta-wysoka" style={{
                        background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
                        color: '#4a148c',
                        textDecoration: 'none',
                        padding: '24px 20px',
                        borderRadius: '16px',
                        fontWeight: '700',
                        fontSize: '1.1rem',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{ fontSize: '2rem' }}>⬆️</span>
                        {getTranslation('highPoint', language)}
                      </Link>
                      <Link to="/trening/puenta-niska" style={{
                        background: 'linear-gradient(135deg, #fff3e0 0%, #ffcc80 100%)',
                        color: '#e65100',
                        textDecoration: 'none',
                        padding: '24px 20px',
                        borderRadius: '16px',
                        fontWeight: '700',
                        fontSize: '1.1rem',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{ fontSize: '2rem' }}>⬇️</span>
                        {getTranslation('lowPoint', language)}
                      </Link>
                      <Link to="/trening/przeglad" style={{
                        background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)',
                        color: '#880e4f',
                        textDecoration: 'none',
                        padding: '24px 20px',
                        borderRadius: '16px',
                        fontWeight: '700',
                        fontSize: '1.1rem',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{ fontSize: '2rem' }}>📋</span>
                        {getTranslation('trainingReview', language)}
                      </Link>
                      <Link to="/trening/statystyki" style={{
                        background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
                        color: '#1b5e20',
                        textDecoration: 'none',
                        padding: '24px 20px',
                        borderRadius: '16px',
                        fontWeight: '700',
                        fontSize: '1.1rem',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{ fontSize: '2rem' }}>📊</span>
                        {getTranslation('trainingStats', language)}
                      </Link>
                    </div>
                  </div>
                </div>
              } />
              <Route path="/trening/strzal-w-kule" element={
                <StrzalWKulePage onTrainingSaved={refreshTrainings} />
              } />
              <Route path="/trening/strzal-w-swinke" element={
                <StrzalWSwinkePage onTrainingSaved={refreshTrainings} />
              } />
              <Route path="/trening/puenta-wysoka" element={
                <PuentaWysokaPage onTrainingSaved={refreshTrainings} />
              } />
              <Route path="/trening/puenta-niska" element={
                <PuentaNiskaPage onTrainingSaved={refreshTrainings} />
              } />
              <Route path="/trening/przeglad" element={
                <PrzegladTreningowPage />
              } />
              <Route path="/trening/statystyki" element={
                <StatystykiKulPage trainings={trainings} />
              } />
              <Route path="/ustawienia" element={<UstawieniaPage />} />
              <Route path="/usun-konto" element={<DeleteAccountPage />} />
            </Routes>
            </div>
          } />
        </Routes>
      </div>
  );
}

function UstawieniaPage() {
  console.log('UstawieniaPage component is being rendered!');
  const { language } = useLanguage();
  const [userProfile, setUserProfile] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    license: '',
    club: ''
  });

  // Fetch user profile and clubs on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = sessionStorage.getItem('access_token');
        
        // Fetch user profile with club info
        const profileResponse = await fetch('/api/user/profile/with-club', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          setUserProfile(profileData);
          setFormData({
            firstname: profileData.firstname || '',
            lastname: profileData.lastname || '',
            license: profileData.license || '',
            club: profileData.club ? profileData.club.id_club : ''
          });
        } else {
          setError(getTranslation('blad-pobierania-profilu-uzytkownika', language));
        }

        // Fetch clubs
        const clubsResponse = await fetch('/api/clubs', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (clubsResponse.ok) {
          const clubsData = await clubsResponse.json();
          setClubs(clubsData);
        } else {
          setError(getTranslation('blad-pobierania-listy-klubow', language));
        }
      } catch (err) {
        setError(getTranslation('blad-polaczenia-z-serwerem', language));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setMessage('');
  };

  const handleSave = async () => {
    // Validate required fields
    if (!formData.firstname.trim() || !formData.lastname.trim()) {
      setMessage(getTranslation('imie-i-nazwisko-sa-obowiazkowe', language));
      return;
    }

    try {
      const token = sessionStorage.getItem('access_token');
      const response = await fetch('/api/user/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstname: formData.firstname.trim(),
          lastname: formData.lastname.trim(),
          license: formData.license ? parseInt(formData.license) : null,
          club: formData.club ? parseInt(formData.club) : null
        }),
      });

      if (response.ok) {
        // Refetch user profile with club details to get updated clubcity
        const profileResponse = await fetch('/api/user/profile/with-club', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (profileResponse.ok) {
          const updatedProfileWithClub = await profileResponse.json();
          setUserProfile(updatedProfileWithClub);
        } else {
          // Fallback to basic profile update if club details fetch fails
          const updatedProfile = await response.json();
          setUserProfile(prev => ({
            ...prev,
            ...updatedProfile
          }));
        }
        
        setIsEditing(false);
        setMessage(getTranslation('profil-zostal-zaktualizowany-pomyslnie', language));
      } else {
        setMessage(getTranslation('blad-aktualizacji-profilu', language));
      }
    } catch (err) {
      setMessage(getTranslation('blad-polaczenia-z-serwerem', language));
    }
  };

  const handleCancel = () => {
    // Reset form data to current profile
    setFormData({
      firstname: userProfile?.firstname || '',
      lastname: userProfile?.lastname || '',
      license: userProfile?.license || '',
      club: userProfile?.club ? userProfile.club.id_club : ''
    });
    setIsEditing(false);
    setMessage('');
  };

  if (loading) {
    return (
      <div style={{ 
        position: 'relative', 
        minHeight: '100vh', 
        padding: 20, 
        background: '#f0f8ff',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {/* Language Selector */}
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
          <LanguageSelector />
        </div>
        
        {/* Main Title */}
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center'
        }}>
          {getTranslation('shootMe', language)}
        </h1>
        
        {/* Subtitle */}
        <p style={{
          fontSize: '1.2rem',
          color: '#6b7280',
          margin: '0 auto 48px auto',
          textAlign: 'center',
          maxWidth: '600px',
          lineHeight: '1.6'
        }}>
          {getTranslation('ustawienia-profilu-uzytkownika', language)}
        </p>

        {/* Back button and breadcrumbs in horizontal layout */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: '20px',
          margin: '0 0 32px 0'
        }}>
          <BackButton language={language} />
          <Breadcrumbs language={language} />
        </div>
        
        <div style={{
          background: 'white',
          borderRadius: 20,
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
          maxWidth: window.innerWidth <= 600 ? '90%' : 500,
          width: window.innerWidth <= 600 ? '90%' : 500,
          padding: window.innerWidth <= 600 ? 28 : 32,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          margin: '0 auto'
        }}>
          <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>{getTranslation('ladowanie', language)}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        position: 'relative', 
        minHeight: '100vh', 
        padding: 20, 
        background: '#f0f8ff',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {/* Language Selector */}
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
          <LanguageSelector />
        </div>
        
        {/* Main Title */}
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center'
        }}>
          {getTranslation('shootMe', language)}
        </h1>
        
        {/* Subtitle */}
        <p style={{
          fontSize: '1.2rem',
          color: '#6b7280',
          margin: '0 auto 48px auto',
          textAlign: 'center',
          maxWidth: '600px',
          lineHeight: '1.6'
        }}>
          {getTranslation('ustawienia-profilu-uzytkownika', language)}
        </p>

        {/* Back button and breadcrumbs in horizontal layout */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: '20px',
          margin: '0 0 32px 0'
        }}>
          <BackButton language={language} />
          <Breadcrumbs language={language} />
        </div>
        
        <div style={{
          background: 'white',
          borderRadius: 20,
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
          maxWidth: window.innerWidth <= 600 ? '90%' : 500,
          width: window.innerWidth <= 600 ? '90%' : 500,
          padding: window.innerWidth <= 600 ? 28 : 32,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          margin: '0 auto'
        }}>
          <p style={{ color: '#c62828', fontSize: '1.1rem' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'relative', 
      minHeight: '100vh', 
      padding: 20, 
      background: '#f0f8ff',
      fontFamily: 'Nunito, Arial, sans-serif'
    }}>
      {/* Language Selector */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <LanguageSelector />
      </div>
      
      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      
      {/* Subtitle */}
      <p style={{
        fontSize: '1.2rem',
        color: '#6b7280',
        margin: '0 auto 48px auto',
        textAlign: 'center',
        maxWidth: '600px',
        lineHeight: '1.6'
      }}>
        {getTranslation('ustawienia-profilu-uzytkownika', language)}
      </p>

      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '0 0 32px 0'
      }}>
        <BackButton language={language} />
        <Breadcrumbs language={language} />
      </div>
      
      <div style={{
        background: 'white',
        borderRadius: 20,
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
        maxWidth: window.innerWidth <= 600 ? '90%' : 500,
        width: window.innerWidth <= 600 ? '90%' : 500,
        padding: window.innerWidth <= 600 ? 28 : 32,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        margin: '0 auto'
      }}>
        <div style={{ marginBottom: 18, width: '100%' }}>
          <label style={{ 
            color: '#4b4b4b', 
            fontWeight: 500, 
            display: 'block', 
            marginBottom: 8
          }}>
            {getTranslation('imie', language)}:
          </label>
          <input
            type="text"
            value={formData.firstname}
            onChange={(e) => handleInputChange('firstname', e.target.value)}
            disabled={!isEditing}
            style={{
              width: '100%',
              padding: 12,
              borderRadius: 8,
              border: '1px solid #bdbdbd',
              fontSize: 16,
              backgroundColor: isEditing ? 'white' : '#f5f5f5',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: 18, width: '100%' }}>
          <label style={{ 
            color: '#4b4b4b', 
            fontWeight: 500, 
            display: 'block', 
            marginBottom: 8
          }}>
            {getTranslation('nazwisko', language)}:
          </label>
          <input
            type="text"
            value={formData.lastname}
            onChange={(e) => handleInputChange('lastname', e.target.value)}
            disabled={!isEditing}
            style={{
              width: '100%',
              padding: 12,
              borderRadius: 8,
              border: '1px solid #bdbdbd',
              fontSize: 16,
              backgroundColor: isEditing ? 'white' : '#f5f5f5',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: 18, width: '100%' }}>
          <label style={{ 
            color: '#4b4b4b', 
            fontWeight: 500, 
            display: 'block', 
            marginBottom: 8
          }}>
            {getTranslation('numer-licencji', language)}:
          </label>
          <input
            type="text"
            value={formData.license}
            onChange={(e) => handleInputChange('license', e.target.value)}
            disabled={!isEditing}
            style={{
              width: '100%',
              padding: 12,
              borderRadius: 8,
              border: '1px solid #bdbdbd',
              fontSize: 16,
              backgroundColor: isEditing ? 'white' : '#f5f5f5',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: 18, width: '100%' }}>
          <label style={{ 
            color: '#4b4b4b', 
            fontWeight: 500, 
            display: 'block', 
            marginBottom: 8
          }}>
            {getTranslation('klub', language)}:
          </label>
          <select
            value={formData.club}
            onChange={(e) => handleInputChange('club', e.target.value)}
            disabled={!isEditing}
            style={{
              width: '100%',
              padding: 12,
              borderRadius: 8,
              border: '1px solid #bdbdbd',
              fontSize: 16,
              backgroundColor: isEditing ? 'white' : '#f5f5f5',
              boxSizing: 'border-box'
            }}
          >
            <option value="">{getTranslation('wybierz-klub', language)}</option>
            {clubs.map(club => (
              <option key={club.id_club} value={club.id_club}>
                {club.clubname}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 18, width: '100%' }}>
          <label style={{ 
            color: '#4b4b4b', 
            fontWeight: 500, 
            display: 'block', 
            marginBottom: 8
          }}>
            {getTranslation('miasto', language)}:
          </label>
          <input
            type="text"
            value={userProfile?.club?.clubcity || ''}
            disabled
            style={{
              width: '100%',
              padding: 12,
              borderRadius: 8,
              border: '1px solid #bdbdbd',
              fontSize: 16,
              backgroundColor: '#f5f5f5',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {message && (
          <div style={{ 
            marginBottom: 18, 
            padding: 12, 
            borderRadius: 8,
            backgroundColor: message.includes('pomyślnie') || message.includes('successfully') || message.includes('succès') ? '#e8f5e8' : '#ffebee',
            color: message.includes('pomyślnie') || message.includes('successfully') || message.includes('succès') ? '#2e7d32' : '#c62828',
            width: '100%',
            textAlign: 'center'
          }}>
            {message}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', width: '100%' }}>
          {!isEditing ? (
            <button
              onClick={handleEdit}
              style={{
                padding: '12px 24px',
                borderRadius: 8,
                border: 'none',
                background: '#7c3aed',
                color: 'white',
                fontWeight: 600,
                fontSize: 16,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)'
              }}
            >
              {getTranslation('edytuj', language)}
            </button>
          ) : (
            <>
              <button
                onClick={handleSave}
                style={{
                  padding: '12px 24px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#7c3aed',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)'
                }}
              >
                {getTranslation('zapisz', language)}
              </button>
              <button
                onClick={handleCancel}
                style={{
                  padding: '12px 24px',
                  borderRadius: 8,
                  border: '2px solid #7c3aed',
                  background: 'white',
                  color: '#7c3aed',
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {getTranslation('anuluj', language)}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DeleteAccountPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const handleDeleteAccount = async () => {
    try {
      const token = sessionStorage.getItem('access_token');
      const response = await fetch('/api/user/delete', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Clear session and redirect to main page
        sessionStorage.removeItem('access_token');
        window.location.href = '/';
      } else {
        console.error('Failed to delete account');
        alert(getTranslation('wystapil-blad-podczas-usuwania-konta', language));
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      alert(getTranslation('wystapil-blad-podczas-usuwania-konta', language));
    }
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff', fontFamily: 'Nunito, Arial, sans-serif' }}>
      {/* Language Selector */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <LanguageSelector />
      </div>
      
      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton language={language} />
        <Breadcrumbs language={language} />
      </div>

      {/* Confirmation Question */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        <p style={{
          fontSize: '1.3rem',
          color: '#333',
          lineHeight: '1.6',
          marginBottom: '40px',
          fontWeight: '500',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          {getTranslation('czy-jestes-pewien-usunac-konto', language)}
        </p>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '20px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={handleDeleteAccount}
            style={{
              background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
              color: '#c62828',
              padding: '16px 32px',
              borderRadius: '12px',
              fontWeight: '700',
              fontSize: '1.1rem',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              transition: 'transform 0.3s ease',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            {getTranslation('tak', language)}
          </button>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
              color: '#2e7d32',
              padding: '16px 32px',
              borderRadius: '12px',
              fontWeight: '700',
              fontSize: '1.1rem',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              transition: 'transform 0.3s ease',
              fontFamily: 'Nunito, Arial, sans-serif'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            {getTranslation('nie', language)}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatystykiKulPage({ trainings }) {
  const { language } = useLanguage();
  // Controls - use Polish values for state (default language)
  const [trainingType, setTrainingType] = useState('strzał w kulę');
  const [timeRange, setTimeRange] = useState('dzień');
  const [dataType, setDataType] = useState('ilość trafień');
  const [distance, setDistance] = useState('wszystkie');

  // Training type mapping - use Polish values
  const trainingTypeMap = {
    'strzał w kulę': 1,
    'strzał w świnkę': 2,
    'puenta wysoka': 3,
    'puenta niska': 4
  };

  // Filter trainings by selected type
  const selectedTrainingType = trainingTypeMap[trainingType];
  const filteredTrainings = trainings ? trainings.filter(t => t.type === selectedTrainingType) : [];

  // Helper: get date key for grouping
  function getDateKey(dateStr) {
    try {
      // Debug the incoming date string
      console.log('Processing date:', dateStr, 'type:', typeof dateStr, 'timeRange:', timeRange);
      
      // Handle different date formats
      let d;
      if (typeof dateStr === 'string' && dateStr.includes('-')) {
        const dateOnly = dateStr.split('T')[0]; // Remove time part if present
        const [year, month, day] = dateOnly.split('-').map(Number);
        // Create date in local timezone to avoid timezone shifts
        d = new Date(year, month - 1, day, 12, 0, 0); // Use noon to avoid timezone issues
      } else {
        // Fallback: try to create a Date object
        d = new Date(dateStr);
      }
      
      if (isNaN(d.getTime())) {
        console.error('Invalid date:', dateStr);
        return null;
      }
      
      // Generate appropriate key based on timeRange
      if (timeRange === 'dzień') {
        // Use local date formatting to avoid timezone issues
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`; // YYYY-MM-DD
      } else if (timeRange === 'tydzień') {
        // Group by week - get the start of the week (Monday)
        const startOfWeek = new Date(d);
        const day = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
        startOfWeek.setDate(diff);
        const year = startOfWeek.getFullYear();
        const month = String(startOfWeek.getMonth() + 1).padStart(2, '0');
        const dayOfMonth = String(startOfWeek.getDate()).padStart(2, '0');
        return `${year}-${month}-${dayOfMonth}`; // YYYY-MM-DD (start of week)
      } else if (timeRange === 'miesiąc') {
        return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'); // YYYY-MM
      } else if (timeRange === 'rok') {
        return String(d.getFullYear()); // YYYY
      }
    } catch (error) {
      console.error('Error in getDateKey:', error, 'for dateStr:', dateStr);
      return null;
    }
  }

  // Helper: get label for X axis
  function getLabel(key) {
    if (timeRange === 'dzień') return key.slice(5); // MM-DD
    if (timeRange === 'tydzień') return key.slice(5); // MM-DD (start of week)
    if (timeRange === 'miesiąc') return key.slice(0, 7); // YYYY-MM
    if (timeRange === 'rok') return key; // YYYY
    return key;
  }

  // Filter by distance
  const distKeys = ['6_5', '7_5', '8_5', '9_5'];
  const distLabels = ['6,5 m', '7,5 m', '8,5 m', '9,5 m'];
  let selectedDist = distance === 'wszystkie' ? distKeys : [distKeys[distLabels.indexOf(distance)]];

  // Generate complete date range based on timeRange
  function generateDateRange() {
    const today = new Date();
    const dates = [];
    
    if (timeRange === 'dzień') {
      // Last 14 days (2 weeks)
      for (let i = 13; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        dates.push(date.toISOString().slice(0, 10));
      }
    } else if (timeRange === 'tydzień') {
      // Last 10 weeks - generate week start dates
      for (let i = 9; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - (i * 7));
        // Get the start of the week (Monday)
        const day = date.getDay();
        const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
        date.setDate(diff);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const dayOfMonth = String(date.getDate()).padStart(2, '0');
        dates.push(`${year}-${month}-${dayOfMonth}`);
      }
    } else if (timeRange === 'miesiąc') {
      // Last 12 months
      for (let i = 11; i >= 0; i--) {
        const date = new Date(today);
        date.setMonth(today.getMonth() - i);
        dates.push(date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0'));
      }
    } else if (timeRange === 'rok') {
      // Last 10 years
      for (let i = 9; i >= 0; i--) {
        const date = new Date(today);
        date.setFullYear(today.getFullYear() - i);
        dates.push(String(date.getFullYear()));
      }
    }
    
    return dates;
  }

  // Group training data
  let grouped = {};
  filteredTrainings.forEach(t => {
    const key = getDateKey(t.date);
    if (key) {
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(t);
    }
  });

  // Generate complete date range
  const sortedKeys = generateDateRange();

  // Prepare chart data
  let labels = sortedKeys.map(getLabel);
  let datasets = [];

  if (dataType === 'ilość trafień') {
    const getSum = (t, key) => selectedDist.reduce((sum, d) => sum + (t[`${key}_${d}`] || 0), 0);
    
    if (trainingType === 'strzał w kulę') {
      // 4 lines: carreau, carreau+trafiony, carreau+trafiony+dotknięcie, wszystkie
      const dataCarreau = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        return trainings.reduce((sum, t) => sum + getSum(t, 'carreau'), 0);
      });
      const dataCarreauHit = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        return trainings.reduce((sum, t) => sum + getSum(t, 'carreau') + getSum(t, 'hit'), 0);
      });
      const dataCarreauHitTouch = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        return trainings.reduce((sum, t) => sum + getSum(t, 'carreau') + getSum(t, 'hit') + getSum(t, 'touch'), 0);
      });
      const dataAll = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        return trainings.reduce((sum, t) => sum + getSum(t, 'carreau') + getSum(t, 'hit') + getSum(t, 'touch') + getSum(t, 'missed'), 0);
      });
      datasets = [
        { label: getTranslation('carreau', language), data: dataCarreau, borderColor: '#1976d2', backgroundColor: '#1976d2', tension: 0.2 },
        { label: getTranslation('carreauHit', language), data: dataCarreauHit, borderColor: '#43a047', backgroundColor: '#43a047', tension: 0.2 },
        { label: getTranslation('carreauHitTouch', language), data: dataCarreauHitTouch, borderColor: '#fbc02d', backgroundColor: '#fbc02d', tension: 0.2 },
        { label: getTranslation('throwCount', language), data: dataAll, borderColor: '#c62828', backgroundColor: '#c62828', tension: 0.2 },
      ];
    } else if (trainingType === 'puenta wysoka' || trainingType === 'puenta niska') {
      // For puenta training types: <20 cm, 0-50 cm, wszystkie
      const dataHit = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        return trainings.reduce((sum, t) => sum + getSum(t, 'hit'), 0);
      });
      const dataHitTouch = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        return trainings.reduce((sum, t) => sum + getSum(t, 'hit') + getSum(t, 'touch'), 0);
      });
      const dataAll = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        return trainings.reduce((sum, t) => sum + getSum(t, 'hit') + getSum(t, 'touch') + getSum(t, 'missed'), 0);
      });
      datasets = [
        { label: getTranslation('under20cm', language), data: dataHit, borderColor: '#43a047', backgroundColor: '#43a047', tension: 0.2 },
        { label: getTranslation('under50cm', language), data: dataHitTouch, borderColor: '#fbc02d', backgroundColor: '#fbc02d', tension: 0.2 },
        { label: getTranslation('throwCount', language), data: dataAll, borderColor: '#c62828', backgroundColor: '#c62828', tension: 0.2 },
      ];
    } else {
      // For strzał w świnkę: trafienie, trafienie+dotknięcie, wszystkie
      const dataHit = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        return trainings.reduce((sum, t) => sum + getSum(t, 'hit'), 0);
      });
      const dataHitTouch = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        return trainings.reduce((sum, t) => sum + getSum(t, 'hit') + getSum(t, 'touch'), 0);
      });
      const dataAll = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        return trainings.reduce((sum, t) => sum + getSum(t, 'hit') + getSum(t, 'touch') + getSum(t, 'missed'), 0);
      });
      datasets = [
        { label: getTranslation('hit', language), data: dataHit, borderColor: '#43a047', backgroundColor: '#43a047', tension: 0.2 },
        { label: getTranslation('hitTouch', language), data: dataHitTouch, borderColor: '#fbc02d', backgroundColor: '#fbc02d', tension: 0.2 },
        { label: getTranslation('throwCount', language), data: dataAll, borderColor: '#c62828', backgroundColor: '#c62828', tension: 0.2 },
      ];
    }
  } else if (dataType === '% trafień') {
    const getSum = (t, key) => selectedDist.reduce((sum, d) => sum + (t[`${key}_${d}`] || 0), 0);
    const getTotal = t => selectedDist.reduce((sum, d) => sum + (t[`carreau_${d}`] || 0) + (t[`hit_${d}`] || 0) + (t[`touch_${d}`] || 0) + (t[`missed_${d}`] || 0), 0);
    
    if (trainingType === 'strzał w kulę') {
      // 3 lines: carreau, carreau+trafienie, carreau+trafienie+dotknięcie
      const dataCarreau = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        const hits = trainings.reduce((sum, t) => sum + getSum(t, 'carreau'), 0);
        const total = trainings.reduce((sum, t) => sum + getTotal(t), 0);
        return total ? Math.round((100 * hits / total) * 100) / 100 : 0;
      });
      const dataCarreauHit = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        const hits = trainings.reduce((sum, t) => sum + getSum(t, 'carreau') + getSum(t, 'hit'), 0);
        const total = trainings.reduce((sum, t) => sum + getTotal(t), 0);
        return total ? Math.round((100 * hits / total) * 100) / 100 : 0;
      });
      const dataCarreauHitTouch = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        const hits = trainings.reduce((sum, t) => sum + getSum(t, 'carreau') + getSum(t, 'hit') + getSum(t, 'touch'), 0);
        const total = trainings.reduce((sum, t) => sum + getTotal(t), 0);
        return total ? Math.round((100 * hits / total) * 100) / 100 : 0;
      });
      datasets = [
        { label: getTranslation('carreau', language), data: dataCarreau, borderColor: '#1976d2', backgroundColor: '#1976d2', tension: 0.2 },
        { label: getTranslation('carreauHit', language), data: dataCarreauHit, borderColor: '#43a047', backgroundColor: '#43a047', tension: 0.2 },
        { label: getTranslation('carreauHitTouch', language), data: dataCarreauHitTouch, borderColor: '#fbc02d', backgroundColor: '#fbc02d', tension: 0.2 },
      ];
    } else if (trainingType === 'puenta wysoka' || trainingType === 'puenta niska') {
      // For puenta training types: <20 cm, 0-50 cm
      const dataHit = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        const hits = trainings.reduce((sum, t) => sum + getSum(t, 'hit'), 0);
        const total = trainings.reduce((sum, t) => sum + getTotal(t), 0);
        return total ? Math.round((100 * hits / total) * 100) / 100 : 0;
      });
      const dataHitTouch = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        const hits = trainings.reduce((sum, t) => sum + getSum(t, 'hit') + getSum(t, 'touch'), 0);
        const total = trainings.reduce((sum, t) => sum + getTotal(t), 0);
        return total ? Math.round((100 * hits / total) * 100) / 100 : 0;
      });
      datasets = [
        { label: getTranslation('under20cm', language), data: dataHit, borderColor: '#43a047', backgroundColor: '#43a047', tension: 0.2 },
        { label: getTranslation('under50cm', language), data: dataHitTouch, borderColor: '#fbc02d', backgroundColor: '#fbc02d', tension: 0.2 },
      ];
    } else {
      // For strzał w świnkę: trafienie, trafienie+dotknięcie
      const dataHit = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        const hits = trainings.reduce((sum, t) => sum + getSum(t, 'hit'), 0);
        const total = trainings.reduce((sum, t) => sum + getTotal(t), 0);
        return total ? Math.round((100 * hits / total) * 100) / 100 : 0;
      });
      const dataHitTouch = sortedKeys.map(k => {
        const trainings = grouped[k] || [];
        const hits = trainings.reduce((sum, t) => sum + getSum(t, 'hit') + getSum(t, 'touch'), 0);
        const total = trainings.reduce((sum, t) => sum + getTotal(t), 0);
        return total ? Math.round((100 * hits / total) * 100) / 100 : 0;
      });
      datasets = [
        { label: getTranslation('hit', language), data: dataHit, borderColor: '#43a047', backgroundColor: '#43a047', tension: 0.2 },
        { label: getTranslation('hitTouch', language), data: dataHitTouch, borderColor: '#fbc02d', backgroundColor: '#fbc02d', tension: 0.2 },
      ];
    }
  } else if (dataType === 'ilość rzutów') {
    // 1 line: all throws
    const getTotal = t => selectedDist.reduce((sum, d) => sum + (t[`carreau_${d}`] || 0) + (t[`hit_${d}`] || 0) + (t[`touch_${d}`] || 0) + (t[`missed_${d}`] || 0), 0);
    const dataThrows = sortedKeys.map(k => {
      const trainings = grouped[k] || [];
      return trainings.reduce((sum, t) => sum + getTotal(t), 0);
    });
    datasets = [
      { label: getTranslation('throwCount', language), data: dataThrows, borderColor: '#1976d2', backgroundColor: '#1976d2', tension: 0.2 },
    ];
  }

  // Validate data before creating chart
  const validLabels = Array.isArray(labels) ? labels : [];
  const validDatasets = Array.isArray(datasets) ? datasets : [];
  
  const chartData = { 
    labels: validLabels, 
    datasets: validDatasets.map(dataset => ({
      ...dataset,
      data: Array.isArray(dataset.data) ? dataset.data : []
    }))
  };
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { 
        display: true,
        text: trainingType === 'strzał w kulę' ? getTranslation('shotAtBoule', language) :
              trainingType === 'strzał w świnkę' ? getTranslation('shotAtCochonnet', language) :
              trainingType === 'puenta wysoka' ? getTranslation('highPointing', language) :
              trainingType === 'puenta niska' ? getTranslation('lowPointing', language) : trainingType,
        font: {
          size: 18,
          family: 'Nunito, Arial, sans-serif',
          weight: 'bold'
        },
        color: '#333'
      },
      tooltip: { 
        mode: 'index', 
        intersect: false,
        callbacks: {
          title: function(context) {
            return getTranslation('date', language) + ': ' + context[0].label;
          },
          label: function(context) {
            const value = context.parsed.y;
            // Add % symbol for percentage data type
            const formattedValue = dataType === '% trafień' ? value + '%' : value;
            return context.dataset.label + ': ' + formattedValue;
          }
        }
      }
    },
    interaction: { mode: 'nearest', axis: 'x', intersect: false },
    scales: {
      y: { 
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            // Add % symbol for percentage data type
            return dataType === '% trafień' ? value + '%' : value;
          }
        }
      },
    },
    elements: {
      point: {
        radius: 6,
        hoverRadius: 8,
        backgroundColor: function(context) {
          return context.dataset.backgroundColor;
        }
      },
      line: {
        borderWidth: 3
      }
    }
  };

  // Debug logging
  console.log('StatystykiKulPage render:', {
    trainingType,
    timeRange,
    trainingsCount: trainings ? trainings.length : 0,
    filteredTrainingsCount: filteredTrainings.length,
    allTrainings: trainings ? trainings.map(t => ({ id: t.id, type: t.type, date: t.date })) : [],
    filteredTrainings: filteredTrainings.map(t => ({ id: t.id, date: t.date, key: getDateKey(t.date) })),
    groupedKeys: Object.keys(grouped),
    sortedKeys,
    labels: validLabels,
    datasetsCount: validDatasets.length,
    chartData
  });

  // UI controls
  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20 }}>
      {/* Language Selector - Top Right */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>

      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton />
        <Breadcrumbs />
      </div>
      {/* Line 1: Training type */}
      <div style={{ display: 'flex', alignItems: 'center', margin: '32px 0 16px 40px', gap: 24 }}>
        <div>
          <span style={{ marginRight: 8, color: '#888', fontWeight: 600 }}>{getTranslation('training', language)}:</span>
          {['strzał w kulę', 'strzał w świnkę', 'puenta wysoka', 'puenta niska'].map(opt => (
            <button
              key={opt}
              onClick={() => setTrainingType(opt)}
              style={{
                marginRight: 8,
                padding: '8px 16px',
                borderRadius: 6,
                border: trainingType === opt ? '2px solid #1976d2' : '1px solid #bdbdbd',
                background: trainingType === opt ? '#e3f2fd' : 'white',
                color: '#222',
                fontWeight: 600,
                fontSize: 15,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {opt === 'strzał w kulę' ? getTranslation('shotAtBoule', language) :
               opt === 'strzał w świnkę' ? getTranslation('shotAtCochonnet', language) :
               opt === 'puenta wysoka' ? getTranslation('highPointing', language) :
               opt === 'puenta niska' ? getTranslation('lowPointing', language) : opt}
            </button>
          ))}
        </div>
      </div>
      
      {/* Line 2: Time range */}
      <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 16px 40px', gap: 24 }}>
        <div>
          <span style={{ marginRight: 8, color: '#888', fontWeight: 600 }}>{getTranslation('range', language)}:</span>
          {['dzień', 'tydzień', 'miesiąc', 'rok'].map(opt => (
            <button
              key={opt}
              onClick={() => setTimeRange(opt)}
              style={{
                marginRight: 8,
                padding: '8px 16px',
                borderRadius: 6,
                border: timeRange === opt ? '2px solid #1976d2' : '1px solid #bdbdbd',
                background: timeRange === opt ? '#e3f2fd' : 'white',
                color: '#222',
                fontWeight: 600,
                fontSize: 15,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {opt === 'dzień' ? getTranslation('day', language) :
               opt === 'tydzień' ? getTranslation('week', language) :
               opt === 'miesiąc' ? getTranslation('month', language) :
               opt === 'rok' ? getTranslation('year', language) : opt}
            </button>
          ))}
        </div>
      </div>
      
      {/* Line 3: Data type */}
      <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 16px 40px', gap: 24 }}>
        <div>
          <span style={{ marginRight: 8, color: '#888', fontWeight: 600 }}>{getTranslation('statistics', language)}:</span>
          {['ilość trafień', '% trafień', 'ilość rzutów'].map(opt => (
            <button
              key={opt}
              onClick={() => setDataType(opt)}
              style={{
                marginRight: 8,
                padding: '8px 16px',
                borderRadius: 6,
                border: dataType === opt ? '2px solid #1976d2' : '1px solid #bdbdbd',
                background: dataType === opt ? '#e3f2fd' : 'white',
                color: '#222',
                fontWeight: 600,
                fontSize: 15,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {opt === 'ilość trafień' ? getTranslation('hitCount', language) :
               opt === '% trafień' ? getTranslation('hitPercentage', language) :
               opt === 'ilość rzutów' ? getTranslation('throwCount', language) : opt}
            </button>
          ))}
        </div>
      </div>
      
      {/* Line 4: Distance */}
      <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 16px 40px', gap: 24 }}>
        <div>
          <span style={{ marginRight: 8, color: '#888', fontWeight: 600 }}>{getTranslation('distance', language)}:</span>
          {['wszystkie', ...distLabels].map(opt => (
            <button
              key={opt}
              onClick={() => setDistance(opt)}
              style={{
                marginRight: 8,
                padding: '8px 16px',
                borderRadius: 6,
                border: distance === opt ? '2px solid #1976d2' : '1px solid #bdbdbd',
                background: distance === opt ? '#e3f2fd' : 'white',
                color: '#222',
                fontWeight: 600,
                fontSize: 15,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {opt === 'wszystkie' ? getTranslation('all', language) : opt}
            </button>
          ))}
        </div>
      </div>
      <div style={{ margin: '0 40px', background: 'white', borderRadius: 16, boxShadow: '0 4px 24px rgba(31,38,135,0.08)', padding: 24 }}>
        {validLabels.length > 0 && validDatasets.length > 0 ? (
          <div style={{ height: 400, width: '100%' }}>
            <Line 
              data={chartData} 
              options={chartOptions}
            />
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: 400, 
            color: '#888', 
            fontSize: 16,
            fontFamily: 'Nunito, Arial, sans-serif'
          }}>
            {getTranslation('noData', language)} {getTranslation('forSelectedCriteria', language)}
          </div>
        )}
      </div>
    </div>
  );
}

function StrzalWKulePage({ onTrainingSaved }) {
  const { language } = useLanguage();
  
  // Helper function to translate database values
  const translateDatabaseValue = (value) => {
    if (!value) return value;
    
    // Debug: log the actual values from database
    console.log('Translating database value:', value, 'for language:', language);
    
    // Common terrain translations
    const terrainTranslations = {
      'pl': {
        'Trawa': 'Trawa',
        'trawa': 'Trawa',
        'Piasek': 'Piasek',
        'piasek': 'Piasek',
        'Ziemia': 'Ziemia',
        'ziemia': 'Ziemia',
        'Asfalt': 'Asfalt',
        'asfalt': 'Asfalt',
        'Beton': 'Beton',
        'beton': 'Beton',
        'Żwir': 'Żwir',
        'żwir': 'Żwir',
        'Miękko': 'Miękko',
        'miękko': 'Miękko',
        'Średnio': 'Średnio',
        'średnio': 'Średnio',
        'Twardo': 'Twardo',
        'twardo': 'Twardo'
      },
      'en': {
        'Trawa': 'Grass',
        'trawa': 'Grass',
        'Piasek': 'Sand',
        'piasek': 'Sand',
        'Ziemia': 'Dirt',
        'ziemia': 'Dirt',
        'Asfalt': 'Asphalt',
        'asfalt': 'Asphalt',
        'Beton': 'Concrete',
        'beton': 'Concrete',
        'Żwir': 'Gravel',
        'żwir': 'Gravel',
        'Miękko': 'Soft',
        'miękko': 'Soft',
        'Średnio': 'Medium',
        'średnio': 'Medium',
        'Twardo': 'Hard',
        'twardo': 'Hard'
      },
      'fr': {
        'Trawa': 'Herbe',
        'trawa': 'Herbe',
        'Piasek': 'Sable',
        'piasek': 'Sable',
        'Ziemia': 'Terre',
        'ziemia': 'Terre',
        'Asfalt': 'Asphalte',
        'asfalt': 'Asphalte',
        'Beton': 'Béton',
        'beton': 'Béton',
        'Żwir': 'Gravier',
        'żwir': 'Gravier',
        'Miękko': 'Mou',
        'miękko': 'Mou',
        'Średnio': 'Moyen',
        'średnio': 'Moyen',
        'Twardo': 'Dur',
        'twardo': 'Dur'
      }
    };
    
    // Common weather translations
    const weatherTranslations = {
      'pl': {
        'Słonecznie': 'Słonecznie',
        'słonecznie': 'Słonecznie',
        'Pochmurno': 'Pochmurno',
        'pochmurno': 'Pochmurno',
        'Deszcz': 'Deszcz',
        'deszcz': 'Deszcz',
        'deszczowo': 'Deszcz',
        'Deszczowo': 'Deszcz',
        'Wiatr': 'Wiatr',
        'wiatr': 'Wiatr',
        'Mgła': 'Mgła',
        'mgła': 'Mgła',
        'Śnieg': 'Śnieg',
        'śnieg': 'Śnieg'
      },
      'en': {
        'Słonecznie': 'Sunny',
        'słonecznie': 'Sunny',
        'Pochmurno': 'Cloudy',
        'pochmurno': 'Cloudy',
        'Deszcz': 'Wet',
        'deszcz': 'Wet',
        'deszczowo': 'Wet',
        'Deszczowo': 'Wet',
        'Wiatr': 'Windy',
        'wiatr': 'Windy',
        'Mgła': 'Foggy',
        'mgła': 'Foggy',
        'Śnieg': 'Snow',
        'śnieg': 'Snow'
      },
      'fr': {
        'Słonecznie': 'Ensoleillé',
        'słonecznie': 'Ensoleillé',
        'Pochmurno': 'Nuageux',
        'pochmurno': 'Nuageux',
        'Deszcz': 'Humide',
        'deszcz': 'Humide',
        'deszczowo': 'Humide',
        'Deszczowo': 'Humide',
        'Wiatr': 'Venteux',
        'wiatr': 'Venteux',
        'Mgła': 'Brumeux',
        'mgła': 'Brumeux',
        'Śnieg': 'Neigeux',
        'śnieg': 'Neigeux'
      }
    };
    
    // Try terrain translations first
    if (terrainTranslations[language] && terrainTranslations[language][value]) {
      return terrainTranslations[language][value];
    }
    
    // Try weather translations
    if (weatherTranslations[language] && weatherTranslations[language][value]) {
      return weatherTranslations[language][value];
    }
    
    // Return original value if no translation found
    return value;
  };
  
  const [terrainTypes, setTerrainTypes] = React.useState([]);
  const [weatherTypes, setWeatherTypes] = React.useState([]);
  const [selectedTerrain, setSelectedTerrain] = React.useState('');
  const [selectedWeather, setSelectedWeather] = React.useState('');
  const [temperature, setTemperature] = React.useState('');
  const [date, setDate] = React.useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = React.useState(() => new Date().toTimeString().slice(0,5));
  const distances = ['6,5 m', '7,5 m', '8,5 m', '9,5 m'];
  const columnKeys = ['carreau', 'hit', 'touch', 'missed'];
  const [values, setValues] = React.useState(() => distances.map(() => columnKeys.map(() => 0)));
  const [isSaved, setIsSaved] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState('');

  // Helper to detect mobile
  const isMobile = window.innerWidth <= 600;

  React.useEffect(() => {
    fetch('/api/terrain-types')
      .then(res => res.json())
      .then(data => setTerrainTypes(data));
    fetch('/api/weather-types')
      .then(res => res.json())
      .then(data => setWeatherTypes(data));
  }, []);

  // Only enable save if any value in the 4x4 grid is nonzero
  const isGridChanged = values.flat().some(val => val > 0);

  React.useEffect(() => { setIsSaved(false); }, [values]);

  const handleValueChange = (rowIdx, colIdx, delta) => {
    setValues(prev => prev.map((row, r) =>
      r === rowIdx
        ? row.map((val, c) => c === colIdx ? Math.max(0, val + delta) : val)
        : row
    ));
  };

  const getDistanceNumber = (label) => parseFloat(label.replace(',', '.'));

  const buildTrainingData = () => {
    const data = {};
    distances.forEach((dist, i) => {
      const distanceKey = dist.replace(',', '_').replace(' m', '');
      data[`carreau_${distanceKey}`] = values[0][i];
      data[`hit_${distanceKey}`] = values[1][i];
      data[`touch_${distanceKey}`] = values[2][i];
      data[`missed_${distanceKey}`] = values[3][i];
    });
    return data;
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    if (!date) {
      setMessage(getTranslation('dateRequired', language));
      setSaving(false);
      return;
    }
    try {
      const token = sessionStorage.getItem('access_token');
      const res = await fetch('/api/trainings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          date,
          time,
          terrain: selectedTerrain || null,
          weather: selectedWeather || null,
          temperature: temperature === '' ? null : Number(temperature),
          type: 1,
          ...buildTrainingData(),
        }),
      });
      if (res.ok) {
        setIsSaved(true);
        setMessage(getTranslation('trainingSaved', language));
        // Reset all fields
        setSelectedTerrain('');
        setSelectedWeather('');
        setTemperature('');
        setDate(new Date().toISOString().slice(0, 10));
        setTime(new Date().toTimeString().slice(0,5));
        setValues(distances.map(() => columnKeys.map(() => 0)));
        
        // Refresh main trainings state for statistics page
        if (onTrainingSaved) {
          onTrainingSaved();
        }
      } else {
        const errorText = await res.text();
        setMessage(getTranslation('saveError', language) + ': ' + errorText);
      }
    } catch (e) {
      setMessage(getTranslation('serverConnectionError', language));
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20 }}>
      {/* Language Selector - Top Right */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>

      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton />
        <Breadcrumbs />
      </div>
      <div className="petanka-form-row" style={{ display: 'flex', gap: 24, alignItems: 'center', marginLeft: 40, marginBottom: 32 }}>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('terrain', language)}:</label><br />
          <select value={selectedTerrain} onChange={e => setSelectedTerrain(Number(e.target.value))} style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 120 }}>
            <option value="">{getTranslation('selectOption', language)}</option>
            {terrainTypes.map((t) => <option key={t.terrain_index} value={t.terrain_index}>{translateDatabaseValue(t.terraintype)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('weather', language)}:</label><br />
          <select value={selectedWeather} onChange={e => setSelectedWeather(Number(e.target.value))} style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 120 }}>
            <option value="">{getTranslation('selectOption', language)}</option>
            {weatherTypes.map((w) => <option key={w.weather_index} value={w.weather_index}>{translateDatabaseValue(w.weathertype)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('temperature', language)}:</label><br />
          <input
            type="number"
            value={temperature}
            onChange={e => setTemperature(e.target.value)}
            min={-20}
            max={40}
            style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', width: 80 }}
          />
          <span style={{ marginLeft: 6, fontFamily: 'Nunito, Arial, sans-serif', color: '#888' }}>°C</span>
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('date', language)}:</label><br />
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            required
            style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 120 }}
          />
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('time', language)}:</label><br />
          <input
            type="time"
            value={time}
            onChange={e => setTime(e.target.value)}
            style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 100 }}
          />
        </div>
      </div>

      {/* Table or card layout depending on screen size */}
      {isMobile ? (
        <div style={{ marginLeft: 0, marginTop: 24, fontFamily: 'Nunito, Arial, sans-serif', width: '100%' }}>
          {distances.map((dist, colIdx) => (
            <div key={dist} style={{ background: '#f5f5f5', borderRadius: 16, margin: '0 12px 24px 12px', padding: 16, boxShadow: '0 2px 8px #e0eafc' }}>
              <div style={{ fontWeight: 700, color: '#222', fontSize: 18, marginBottom: 12, textAlign: 'center' }}>{dist}</div>
              {columnKeys.map((colKey, rowIdx) => (
                <div key={colKey} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ fontWeight: 600, color: '#888', fontSize: 16 }}>{getTranslation(colKey, language)}</span>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, -1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginRight: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                      disabled={values[rowIdx][colIdx] <= 0}
                    >-</button>
                    <span style={{ width: 32, textAlign: 'center', fontSize: 20, color: '#222', fontWeight: 600, display: 'inline-block' }}>{values[rowIdx][colIdx]}</span>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, 1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginLeft: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                    >+</button>
                  </div>
                </div>
              ))}
              <div style={{ borderTop: '1px solid #bdbdbd', marginTop: 12, paddingTop: 10, textAlign: 'right', fontWeight: 700, color: '#2e4a21', fontSize: 18 }}>
                {getTranslation('totalShots', language)}: {values.reduce((sum, row) => sum + row[colIdx], 0)}
              </div>
            </div>
          ))}
          <div style={{ textAlign: 'center', fontWeight: 700, color: '#222', fontSize: 18, margin: '12px 0 0 0' }}>
            {getTranslation('totalAllShots', language)}: {values.reduce((sum, row) => sum + row.reduce((a, b) => a + b, 0), 0)}
          </div>
        </div>
      ) : (
        <div style={{ marginLeft: 40, marginTop: 24, fontFamily: 'Nunito, Arial, sans-serif' }}>
          <div style={{ 
            background: '#f5f5f5', 
            borderRadius: 16, 
            padding: 24, 
            boxShadow: '0 2px 8px #e0eafc', 
            marginRight: 40,
            overflowX: 'auto',
            display: 'inline-block'
          }}>
            <div style={{ 
              display: 'flex', 
              fontWeight: 700, 
              color: '#888', 
              fontSize: 18, 
              marginBottom: 16
            }}>
              <div style={{ width: 120, textAlign: 'left', boxSizing: 'border-box', flexShrink: 0 }}>{getTranslation('distance', language)}</div>
              {distances.map((dist, idx) => (
                <div key={dist} style={{ width: 120, textAlign: 'center', boxSizing: 'border-box', marginRight: 12, flexShrink: 0 }}>{dist}</div>
              ))}
              <div style={{ width: 100, textAlign: 'center', color: '#222', boxSizing: 'border-box', flexShrink: 0 }}>{getTranslation('totalShots', language)}</div>
            </div>
            {columnKeys.map((colKey, rowIdx) => (
              <div key={colKey} style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ width: 120, color: '#222', fontWeight: 600, textAlign: 'left', boxSizing: 'border-box', flexShrink: 0 }}>{getTranslation(colKey, language)}</div>
                {distances.map((dist, colIdx) => (
                  <div key={dist} style={{ width: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', marginRight: 12, flexShrink: 0 }}>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, -1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginRight: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                      disabled={values[rowIdx][colIdx] <= 0}
                    >-</button>
                    <span
                      style={{ width: 32, textAlign: 'center', fontSize: 20, color: '#222', fontWeight: 600, display: 'inline-block' }}
                    >{values[rowIdx][colIdx]}</span>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, 1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginLeft: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                    >+</button>
                  </div>
                ))}
                <div style={{ width: 100, textAlign: 'center', color: '#222', fontWeight: 700, fontSize: 18, boxSizing: 'border-box', flexShrink: 0 }}>
                  {values[rowIdx].reduce((a, b) => a + b, 0)}
                </div>
              </div>
            ))}
            {/* Suma rzutów total row */}
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid #bdbdbd' }}>
              <div style={{ width: 120, fontWeight: 700, color: '#222', fontSize: 18, flexShrink: 0 }}>{getTranslation('totalShots', language)}</div>
              {distances.map((dist, colIdx) => (
                <div key={dist} style={{ width: 120, textAlign: 'center', fontWeight: 700, color: '#2e4a21', fontSize: 18, marginRight: 12, flexShrink: 0 }}>
                  {values.reduce((sum, row) => sum + row[colIdx], 0)}
                </div>
              ))}
              <div style={{ width: 100, textAlign: 'center', fontWeight: 700, color: '#222', fontSize: 18, flexShrink: 0 }}>
                {values.reduce((sum, row) => sum + row.reduce((a, b) => a + b, 0), 0)}
              </div>
            </div>
          </div>
        </div>
      )}
      <div style={{ marginLeft: 40, marginTop: 32, display: 'flex', gap: 24 }}>
        <button
          onClick={handleSave}
          disabled={isSaved || saving || !isGridChanged}
          style={{
            padding: '12px 32px',
            borderRadius: 8,
            border: 'none',
            background: isSaved ? '#bdbdbd' : '#6dd5fa',
            color: 'white',
            fontWeight: 700,
            fontSize: 18,
            letterSpacing: 1,
            cursor: isSaved || !isGridChanged ? 'not-allowed' : 'pointer',
            transition: 'background 0.3s',
          }}
        >{getTranslation('saveTraining', language)}</button>
        {message && <span style={{ marginLeft: 24, color: isSaved ? '#2e7d32' : '#c62828', fontWeight: 600, fontSize: 16 }}>{message}</span>}
      </div>
    </div>
  );
}

function StrzalWSwinkePage({ onTrainingSaved }) {
  const { language } = useLanguage();
  
  // Helper function to translate database values
  const translateDatabaseValue = (value) => {
    if (!value) return value;
    
    // Debug: log the actual values from database
    console.log('Translating database value:', value, 'for language:', language);
    
    // Common terrain translations
    const terrainTranslations = {
      'pl': {
        'Trawa': 'Trawa',
        'trawa': 'Trawa',
        'Piasek': 'Piasek',
        'piasek': 'Piasek',
        'Ziemia': 'Ziemia',
        'ziemia': 'Ziemia',
        'Asfalt': 'Asfalt',
        'asfalt': 'Asfalt',
        'Beton': 'Beton',
        'beton': 'Beton',
        'Żwir': 'Żwir',
        'żwir': 'Żwir',
        'Miękko': 'Miękko',
        'miękko': 'Miękko',
        'Średnio': 'Średnio',
        'średnio': 'Średnio',
        'Twardo': 'Twardo',
        'twardo': 'Twardo'
      },
      'en': {
        'Trawa': 'Grass',
        'trawa': 'Grass',
        'Piasek': 'Sand',
        'piasek': 'Sand',
        'Ziemia': 'Dirt',
        'ziemia': 'Dirt',
        'Asfalt': 'Asphalt',
        'asfalt': 'Asphalt',
        'Beton': 'Concrete',
        'beton': 'Concrete',
        'Żwir': 'Gravel',
        'żwir': 'Gravel',
        'Miękko': 'Soft',
        'miękko': 'Soft',
        'Średnio': 'Medium',
        'średnio': 'Medium',
        'Twardo': 'Hard',
        'twardo': 'Hard'
      },
      'fr': {
        'Trawa': 'Herbe',
        'trawa': 'Herbe',
        'Piasek': 'Sable',
        'piasek': 'Sable',
        'Ziemia': 'Terre',
        'ziemia': 'Terre',
        'Asfalt': 'Asphalte',
        'asfalt': 'Asphalte',
        'Beton': 'Béton',
        'beton': 'Béton',
        'Żwir': 'Gravier',
        'żwir': 'Gravier',
        'Miękko': 'Mou',
        'miękko': 'Mou',
        'Średnio': 'Moyen',
        'średnio': 'Moyen',
        'Twardo': 'Dur',
        'twardo': 'Dur'
      }
    };
    
    // Common weather translations
    const weatherTranslations = {
      'pl': {
        'Słonecznie': 'Słonecznie',
        'słonecznie': 'Słonecznie',
        'Pochmurno': 'Pochmurno',
        'pochmurno': 'Pochmurno',
        'Deszcz': 'Deszcz',
        'deszcz': 'Deszcz',
        'deszczowo': 'Deszcz',
        'Deszczowo': 'Deszcz',
        'Wiatr': 'Wiatr',
        'wiatr': 'Wiatr',
        'Mgła': 'Mgła',
        'mgła': 'Mgła',
        'Śnieg': 'Śnieg',
        'śnieg': 'Śnieg'
      },
      'en': {
        'Słonecznie': 'Sunny',
        'słonecznie': 'Sunny',
        'Pochmurno': 'Cloudy',
        'pochmurno': 'Cloudy',
        'Deszcz': 'Wet',
        'deszcz': 'Wet',
        'deszczowo': 'Wet',
        'Deszczowo': 'Wet',
        'Wiatr': 'Windy',
        'wiatr': 'Windy',
        'Mgła': 'Foggy',
        'mgła': 'Foggy',
        'Śnieg': 'Snow',
        'śnieg': 'Snow'
      },
      'fr': {
        'Słonecznie': 'Ensoleillé',
        'słonecznie': 'Ensoleillé',
        'Pochmurno': 'Nuageux',
        'pochmurno': 'Nuageux',
        'Deszcz': 'Humide',
        'deszcz': 'Humide',
        'deszczowo': 'Humide',
        'Deszczowo': 'Humide',
        'Wiatr': 'Venteux',
        'wiatr': 'Venteux',
        'Mgła': 'Brumeux',
        'mgła': 'Brumeux',
        'Śnieg': 'Neigeux',
        'śnieg': 'Neigeux'
      }
    };
    
    // Try terrain translations first
    if (terrainTranslations[language] && terrainTranslations[language][value]) {
      return terrainTranslations[language][value];
    }
    
    // Try weather translations
    if (weatherTranslations[language] && weatherTranslations[language][value]) {
      return weatherTranslations[language][value];
    }
    
    // Return original value if no translation found
    return value;
  };
  
  const [terrainTypes, setTerrainTypes] = React.useState([]);
  const [weatherTypes, setWeatherTypes] = React.useState([]);
  const [selectedTerrain, setSelectedTerrain] = React.useState('');
  const [selectedWeather, setSelectedWeather] = React.useState('');
  const [temperature, setTemperature] = React.useState('');
  const [date, setDate] = React.useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = React.useState(() => new Date().toTimeString().slice(0,5));
  const distances = ['6,5 m', '7,5 m', '8,5 m', '9,5 m'];
  const columnKeys = ['hit', 'touch', 'missed'];
  const [values, setValues] = React.useState(() => columnKeys.map(() => distances.map(() => 0)));
  const [isSaved, setIsSaved] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState('');

  // Helper to detect mobile
  const isMobile = window.innerWidth <= 600;

  React.useEffect(() => {
    fetch('/api/terrain-types')
      .then(res => res.json())
      .then(data => setTerrainTypes(data));
    fetch('/api/weather-types')
      .then(res => res.json())
      .then(data => setWeatherTypes(data));
  }, []);

  // Only enable save if any value in the 3x4 grid is nonzero
  const isGridChanged = values.flat().some(val => val > 0);

  React.useEffect(() => { setIsSaved(false); }, [values]);

  const handleValueChange = (rowIdx, colIdx, delta) => {
    setValues(prev => prev.map((row, r) =>
      r === rowIdx
        ? row.map((val, c) => c === colIdx ? Math.max(0, val + delta) : val)
        : row
    ));
  };

  const getDistanceNumber = (label) => parseFloat(label.replace(',', '.'));

  // Build training data for strzał w świnkę: trafiony->hit, dotknięcie->touch, nietrafiony->missed
  const buildTrainingData = () => {
    const data = {};
    distances.forEach((dist, i) => {
      const distanceKey = dist.replace(',', '_').replace(' m', '');
      // For strzał w świnkę: no carreau, so set to 0
      data[`carreau_${distanceKey}`] = 0;
      // Map: trafiony -> hit, dotknięcie -> touch, nietrafiony -> missed
      data[`hit_${distanceKey}`] = values[0][i];      // trafiony
      data[`touch_${distanceKey}`] = values[1][i];    // dotknięcie
      data[`missed_${distanceKey}`] = values[2][i];   // nietrafiony
    });
    return data;
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    if (!date) {
      setMessage('Data jest wymagana.');
      setSaving(false);
      return;
    }
    try {
      const token = sessionStorage.getItem('access_token');
      const res = await fetch('/api/trainings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          date,
          time,
          terrain: selectedTerrain || null,
          weather: selectedWeather || null,
          temperature: temperature === '' ? null : Number(temperature),
          type: 2,
          ...buildTrainingData(),
        }),
      });
      if (res.ok) {
        setIsSaved(true);
        setMessage('Trening zapisany!');
        // Reset all fields
        setSelectedTerrain('');
        setSelectedWeather('');
        setTemperature('');
        setDate(new Date().toISOString().slice(0, 10));
        setTime(new Date().toTimeString().slice(0,5));
        setValues(columnKeys.map(() => distances.map(() => 0)));
        
        // Refresh main trainings state for statistics page
        if (onTrainingSaved) {
          onTrainingSaved();
        }
      } else {
        const errorText = await res.text();
        setMessage('Błąd zapisu treningu: ' + errorText);
      }
    } catch (e) {
      setMessage('Błąd połączenia z serwerem.');
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20 }}>
      {/* Language Selector - Top Right */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>

      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton />
        <Breadcrumbs />
      </div>
      <div className="petanka-form-row" style={{ display: 'flex', gap: 24, alignItems: 'center', marginLeft: 40, marginBottom: 32 }}>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('terrain', language)}:</label><br />
          <select value={selectedTerrain} onChange={e => setSelectedTerrain(Number(e.target.value))} style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 120 }}>
            <option value="">{getTranslation('selectOption', language)}</option>
            {terrainTypes.map((t) => <option key={t.terrain_index} value={t.terrain_index}>{translateDatabaseValue(t.terraintype)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('weather', language)}:</label><br />
          <select value={selectedWeather} onChange={e => setSelectedWeather(Number(e.target.value))} style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 120 }}>
            <option value="">{getTranslation('selectOption', language)}</option>
            {weatherTypes.map((w) => <option key={w.weather_index} value={w.weather_index}>{translateDatabaseValue(w.weathertype)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('temperature', language)}:</label><br />
          <input
            type="number"
            value={temperature}
            onChange={e => setTemperature(e.target.value)}
            min={-20}
            max={40}
            style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', width: 80 }}
          />
          <span style={{ marginLeft: 6, fontFamily: 'Nunito, Arial, sans-serif', color: '#888' }}>°C</span>
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('date', language)}:</label><br />
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            required
            style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 120 }}
          />
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('time', language)}:</label><br />
          <input
            type="time"
            value={time}
            onChange={e => setTime(e.target.value)}
            style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 100 }}
          />
        </div>
      </div>

      {/* Table or card layout depending on screen size */}
      {isMobile ? (
        <div style={{ marginLeft: 0, marginTop: 24, fontFamily: 'Nunito, Arial, sans-serif', width: '100%' }}>
          {distances.map((dist, colIdx) => (
            <div key={dist} style={{ background: '#f5f5f5', borderRadius: 16, margin: '0 12px 24px 12px', padding: 16, boxShadow: '0 2px 8px #e0eafc' }}>
              <div style={{ fontWeight: 700, color: '#222', fontSize: 18, marginBottom: 12, textAlign: 'center' }}>{dist}</div>
              {columnKeys.map((colKey, rowIdx) => (
                <div key={colKey} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ fontWeight: 600, color: '#888', fontSize: 16 }}>{getTranslation(colKey, language)}</span>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, -1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginRight: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                      disabled={values[rowIdx][colIdx] <= 0}
                    >-</button>
                    <span style={{ width: 32, textAlign: 'center', fontSize: 20, color: '#222', fontWeight: 600, display: 'inline-block' }}>{values[rowIdx][colIdx]}</span>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, 1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginLeft: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                    >+</button>
                  </div>
                </div>
              ))}
              <div style={{ borderTop: '1px solid #bdbdbd', marginTop: 12, paddingTop: 10, textAlign: 'right', fontWeight: 700, color: '#2e4a21', fontSize: 18 }}>
                {getTranslation('totalShots', language)}: {values.reduce((sum, row) => sum + row[colIdx], 0)}
              </div>
            </div>
          ))}
          <div style={{ textAlign: 'center', fontWeight: 700, color: '#222', fontSize: 18, margin: '12px 0 0 0' }}>
            {getTranslation('totalAllShots', language)}: {values.reduce((sum, row) => sum + row.reduce((a, b) => a + b, 0), 0)}
          </div>
        </div>
      ) : (
        <div style={{ marginLeft: 40, marginTop: 24, fontFamily: 'Nunito, Arial, sans-serif' }}>
          <div style={{ 
            background: '#f5f5f5', 
            borderRadius: 16, 
            padding: 24, 
            boxShadow: '0 2px 8px #e0eafc', 
            marginRight: 40,
            overflowX: 'auto',
            display: 'inline-block'
          }}>
            <div style={{ 
              display: 'flex', 
              fontWeight: 700, 
              color: '#888', 
              fontSize: 18, 
              marginBottom: 16
            }}>
              <div style={{ width: 120, textAlign: 'left', boxSizing: 'border-box', flexShrink: 0 }}>{getTranslation('distance', language)}</div>
              {distances.map((dist, idx) => (
                <div key={dist} style={{ width: 120, textAlign: 'center', boxSizing: 'border-box', marginRight: 12, flexShrink: 0 }}>{dist}</div>
              ))}
              <div style={{ width: 100, textAlign: 'center', color: '#222', boxSizing: 'border-box', flexShrink: 0 }}>{getTranslation('totalShots', language)}</div>
            </div>
            {columnKeys.map((colKey, rowIdx) => (
              <div key={colKey} style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ width: 120, color: '#222', fontWeight: 600, textAlign: 'left', boxSizing: 'border-box', flexShrink: 0 }}>{getTranslation(colKey, language)}</div>
                {distances.map((dist, colIdx) => (
                  <div key={dist} style={{ width: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', marginRight: 12, flexShrink: 0 }}>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, -1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginRight: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                      disabled={values[rowIdx][colIdx] <= 0}
                    >-</button>
                    <span
                      style={{ width: 32, textAlign: 'center', fontSize: 20, color: '#222', fontWeight: 600, display: 'inline-block' }}
                    >{values[rowIdx][colIdx]}</span>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, 1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginLeft: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                    >+</button>
                  </div>
                ))}
                <div style={{ width: 100, textAlign: 'center', color: '#222', fontWeight: 700, fontSize: 18, boxSizing: 'border-box', flexShrink: 0 }}>
                  {values[rowIdx].reduce((a, b) => a + b, 0)}
                </div>
              </div>
            ))}
            {/* Suma rzutów total row */}
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid #bdbdbd' }}>
              <div style={{ width: 120, fontWeight: 700, color: '#222', fontSize: 18, flexShrink: 0 }}>{getTranslation('totalShots', language)}</div>
              {distances.map((dist, colIdx) => (
                <div key={dist} style={{ width: 120, textAlign: 'center', fontWeight: 700, color: '#2e4a21', fontSize: 18, marginRight: 12, flexShrink: 0 }}>
                  {values.reduce((sum, row) => sum + row[colIdx], 0)}
                </div>
              ))}
              <div style={{ width: 100, textAlign: 'center', fontWeight: 700, color: '#222', fontSize: 18, flexShrink: 0 }}>
                {values.reduce((sum, row) => sum + row.reduce((a, b) => a + b, 0), 0)}
              </div>
            </div>
          </div>
        </div>
      )}
      <div style={{ marginLeft: 40, marginTop: 32, display: 'flex', gap: 24 }}>
        <button
          onClick={handleSave}
          disabled={isSaved || saving || !isGridChanged}
          style={{
            padding: '12px 32px',
            borderRadius: 8,
            border: 'none',
            background: isSaved ? '#bdbdbd' : '#6dd5fa',
            color: 'white',
            fontWeight: 700,
            fontSize: 18,
            letterSpacing: 1,
            cursor: isSaved || !isGridChanged ? 'not-allowed' : 'pointer',
            transition: 'background 0.3s',
          }}
        >{getTranslation('saveTraining', language)}</button>
        {message && <span style={{ marginLeft: 24, color: isSaved ? '#2e7d32' : '#c62828', fontWeight: 600, fontSize: 16 }}>{message}</span>}
      </div>
    </div>
  );
}

function PuentaWysokaPage() {
  const { language } = useLanguage();
  
  // Helper function to translate database values
  const translateDatabaseValue = (value) => {
    if (!value) return value;
    
    // Debug: log the actual values from database
    console.log('Translating database value:', value, 'for language:', language);
    
    // Common terrain translations
    const terrainTranslations = {
      'pl': {
        'Trawa': 'Trawa',
        'trawa': 'Trawa',
        'Piasek': 'Piasek',
        'piasek': 'Piasek',
        'Ziemia': 'Ziemia',
        'ziemia': 'Ziemia',
        'Asfalt': 'Asfalt',
        'asfalt': 'Asfalt',
        'Beton': 'Beton',
        'beton': 'Beton',
        'Żwir': 'Żwir',
        'żwir': 'Żwir',
        'Miękko': 'Miękko',
        'miękko': 'Miękko',
        'Średnio': 'Średnio',
        'średnio': 'Średnio',
        'Twardo': 'Twardo',
        'twardo': 'Twardo'
      },
      'en': {
        'Trawa': 'Grass',
        'trawa': 'Grass',
        'Piasek': 'Sand',
        'piasek': 'Sand',
        'Ziemia': 'Dirt',
        'ziemia': 'Dirt',
        'Asfalt': 'Asphalt',
        'asfalt': 'Asphalt',
        'Beton': 'Concrete',
        'beton': 'Concrete',
        'Żwir': 'Gravel',
        'żwir': 'Gravel',
        'Miękko': 'Soft',
        'miękko': 'Soft',
        'Średnio': 'Medium',
        'średnio': 'Medium',
        'Twardo': 'Hard',
        'twardo': 'Hard'
      },
      'fr': {
        'Trawa': 'Herbe',
        'trawa': 'Herbe',
        'Piasek': 'Sable',
        'piasek': 'Sable',
        'Ziemia': 'Terre',
        'ziemia': 'Terre',
        'Asfalt': 'Asphalte',
        'asfalt': 'Asphalte',
        'Beton': 'Béton',
        'beton': 'Béton',
        'Żwir': 'Gravier',
        'żwir': 'Gravier',
        'Miękko': 'Mou',
        'miękko': 'Mou',
        'Średnio': 'Moyen',
        'średnio': 'Moyen',
        'Twardo': 'Dur',
        'twardo': 'Dur'
      }
    };
    
    // Common weather translations
    const weatherTranslations = {
      'pl': {
        'Słonecznie': 'Słonecznie',
        'słonecznie': 'Słonecznie',
        'Pochmurno': 'Pochmurno',
        'pochmurno': 'Pochmurno',
        'Deszcz': 'Deszcz',
        'deszcz': 'Deszcz',
        'deszczowo': 'Deszcz',
        'Deszczowo': 'Deszcz',
        'Wiatr': 'Wiatr',
        'wiatr': 'Wiatr',
        'Mgła': 'Mgła',
        'mgła': 'Mgła',
        'Śnieg': 'Śnieg',
        'śnieg': 'Śnieg'
      },
      'en': {
        'Słonecznie': 'Sunny',
        'słonecznie': 'Sunny',
        'Pochmurno': 'Cloudy',
        'pochmurno': 'Cloudy',
        'Deszcz': 'Wet',
        'deszcz': 'Wet',
        'deszczowo': 'Wet',
        'Deszczowo': 'Wet',
        'Wiatr': 'Windy',
        'wiatr': 'Windy',
        'Mgła': 'Foggy',
        'mgła': 'Foggy',
        'Śnieg': 'Snow',
        'śnieg': 'Snow'
      },
      'fr': {
        'Słonecznie': 'Ensoleillé',
        'słonecznie': 'Ensoleillé',
        'Pochmurno': 'Nuageux',
        'pochmurno': 'Nuageux',
        'Deszcz': 'Humide',
        'deszcz': 'Humide',
        'deszczowo': 'Humide',
        'Deszczowo': 'Humide',
        'Wiatr': 'Venteux',
        'wiatr': 'Venteux',
        'Mgła': 'Brumeux',
        'mgła': 'Brumeux',
        'Śnieg': 'Neigeux',
        'śnieg': 'Neigeux'
      }
    };
    
    // Try terrain translations first
    if (terrainTranslations[language] && terrainTranslations[language][value]) {
      return terrainTranslations[language][value];
    }
    
    // Try weather translations
    if (weatherTranslations[language] && weatherTranslations[language][value]) {
      return weatherTranslations[language][value];
    }
    
    // Return original value if no translation found
    return value;
  };
  
  const [terrainTypes, setTerrainTypes] = React.useState([]);
  const [weatherTypes, setWeatherTypes] = React.useState([]);
  const [selectedTerrain, setSelectedTerrain] = React.useState('');
  const [selectedWeather, setSelectedWeather] = React.useState('');
  const [temperature, setTemperature] = React.useState('');
  const [date, setDate] = React.useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = React.useState(() => new Date().toTimeString().slice(0,5));
  const distances = ['6,5 m', '7,5 m', '8,5 m', '9,5 m'];
  const columnKeys = ['<20 cm', '21-50 cm', '>50cm'];
  const [values, setValues] = React.useState(() => columnKeys.map(() => distances.map(() => 0)));
  const [isSaved, setIsSaved] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState('');

  // Helper to detect mobile
  const isMobile = window.innerWidth <= 600;

  React.useEffect(() => {
    fetch('/api/terrain-types')
      .then(res => res.json())
      .then(data => setTerrainTypes(data));
    fetch('/api/weather-types')
      .then(res => res.json())
      .then(data => setWeatherTypes(data));
  }, []);

  // Only enable save if any value in the 3x4 grid is nonzero
  const isGridChanged = values.flat().some(val => val > 0);

  React.useEffect(() => { setIsSaved(false); }, [values]);

  const handleValueChange = (rowIdx, colIdx, delta) => {
    setValues(prev => prev.map((row, r) =>
      r === rowIdx
        ? row.map((val, c) => c === colIdx ? Math.max(0, val + delta) : val)
        : row
    ));
  };

  const getDistanceNumber = (label) => parseFloat(label.replace(',', '.'));

  // Build training data for puenta wysoka: <20cm->hit, 21-50cm->touch, >50cm->missed
  const buildTrainingData = () => {
    const data = {};
    distances.forEach((dist, i) => {
      const distanceKey = dist.replace(',', '_').replace(' m', '');
      // For puenta: no carreau, so set to 0
      data[`carreau_${distanceKey}`] = 0;
      // Map: <20 cm -> hit, 21-50 cm -> touch, >50 cm -> missed
      data[`hit_${distanceKey}`] = values[0][i];      // <20 cm
      data[`touch_${distanceKey}`] = values[1][i];    // 21-50 cm
      data[`missed_${distanceKey}`] = values[2][i];   // >50 cm
    });
    return data;
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    if (!date) {
      setMessage('Data jest wymagana.');
      setSaving(false);
      return;
    }
    try {
      const token = sessionStorage.getItem('access_token');
      const res = await fetch('/api/trainings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          date,
          time,
          terrain: selectedTerrain || null,
          weather: selectedWeather || null,
          temperature: temperature === '' ? null : Number(temperature),
          type: 3,
          ...buildTrainingData(),
        }),
      });
      if (res.ok) {
        setIsSaved(true);
        setMessage('Trening zapisany!');
        // Reset all fields
        setSelectedTerrain('');
        setSelectedWeather('');
        setTemperature('');
        setDate(new Date().toISOString().slice(0, 10));
        setTime(new Date().toTimeString().slice(0,5));
        setValues(columnKeys.map(() => distances.map(() => 0)));
      } else {
        setMessage('Błąd zapisu treningu.');
      }
    } catch (e) {
      setMessage('Błąd połączenia z serwerem.');
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20 }}>
      {/* Language Selector - Top Right */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>

      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton />
        <Breadcrumbs />
      </div>
      <div className="petanka-form-row" style={{ display: 'flex', gap: 24, alignItems: 'center', marginLeft: 40, marginBottom: 32 }}>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('terrain', language)}:</label><br />
          <select value={selectedTerrain} onChange={e => setSelectedTerrain(Number(e.target.value))} style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 120 }}>
            <option value="">{getTranslation('selectOption', language)}</option>
            {terrainTypes.map((t) => <option key={t.terrain_index} value={t.terrain_index}>{translateDatabaseValue(t.terraintype)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('weather', language)}:</label><br />
          <select value={selectedWeather} onChange={e => setSelectedWeather(Number(e.target.value))} style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 120 }}>
            <option value="">{getTranslation('selectOption', language)}</option>
            {weatherTypes.map((w) => <option key={w.weather_index} value={w.weather_index}>{translateDatabaseValue(w.weathertype)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('temperature', language)}:</label><br />
          <input
            type="number"
            value={temperature}
            onChange={e => setTemperature(e.target.value)}
            min={-20}
            max={40}
            style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', width: 80 }}
          />
          <span style={{ marginLeft: 6, fontFamily: 'Nunito, Arial, sans-serif', color: '#888' }}>°C</span>
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('date', language)}:</label><br />
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            required
            style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 120 }}
          />
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('time', language)}:</label><br />
          <input
            type="time"
            value={time}
            onChange={e => setTime(e.target.value)}
            style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 100 }}
          />
        </div>
      </div>

      {/* Table or card layout depending on screen size */}
      {isMobile ? (
        <div style={{ marginLeft: 0, marginTop: 24, fontFamily: 'Nunito, Arial, sans-serif', width: '100%' }}>
          {distances.map((dist, colIdx) => (
            <div key={dist} style={{ background: '#f5f5f5', borderRadius: 16, margin: '0 12px 24px 12px', padding: 16, boxShadow: '0 2px 8px #e0eafc' }}>
              <div style={{ fontWeight: 700, color: '#222', fontSize: 18, marginBottom: 12, textAlign: 'center' }}>{dist}</div>
              {columnKeys.map((colKey, rowIdx) => (
                <div key={colKey} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ fontWeight: 600, color: '#888', fontSize: 16 }}>{getTranslation(colKey, language)}</span>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, -1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginRight: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                      disabled={values[rowIdx][colIdx] <= 0}
                    >-</button>
                    <span style={{ width: 32, textAlign: 'center', fontSize: 20, color: '#222', fontWeight: 600, display: 'inline-block' }}>{values[rowIdx][colIdx]}</span>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, 1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginLeft: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                    >+</button>
                  </div>
                </div>
              ))}
              <div style={{ borderTop: '1px solid #bdbdbd', marginTop: 12, paddingTop: 10, textAlign: 'right', fontWeight: 700, color: '#2e4a21', fontSize: 18 }}>
                {getTranslation('totalShots', language)}: {values.reduce((sum, row) => sum + row[colIdx], 0)}
              </div>
            </div>
          ))}
          <div style={{ textAlign: 'center', fontWeight: 700, color: '#222', fontSize: 18, margin: '12px 0 0 0' }}>
            {getTranslation('totalAllShots', language)}: {values.reduce((sum, row) => sum + row.reduce((a, b) => a + b, 0), 0)}
          </div>
        </div>
      ) : (
        <div style={{ marginLeft: 40, marginTop: 24, fontFamily: 'Nunito, Arial, sans-serif' }}>
          <div style={{ 
            background: '#f5f5f5', 
            borderRadius: 16, 
            padding: 24, 
            boxShadow: '0 2px 8px #e0eafc', 
            marginRight: 40,
            overflowX: 'auto',
            display: 'inline-block'
          }}>
            <div style={{ 
              display: 'flex', 
              fontWeight: 700, 
              color: '#888', 
              fontSize: 18, 
              marginBottom: 16
            }}>
              <div style={{ width: 120, textAlign: 'left', boxSizing: 'border-box', flexShrink: 0 }}>{getTranslation('distance', language)}</div>
              {distances.map((dist, idx) => (
                <div key={dist} style={{ width: 120, textAlign: 'center', boxSizing: 'border-box', marginRight: 12, flexShrink: 0 }}>{dist}</div>
              ))}
              <div style={{ width: 100, textAlign: 'center', color: '#222', boxSizing: 'border-box', flexShrink: 0 }}>{getTranslation('totalShots', language)}</div>
            </div>
            {columnKeys.map((colKey, rowIdx) => (
              <div key={colKey} style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ width: 120, color: '#222', fontWeight: 600, textAlign: 'left', boxSizing: 'border-box', flexShrink: 0 }}>{getTranslation(colKey, language)}</div>
                {distances.map((dist, colIdx) => (
                  <div key={dist} style={{ width: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', marginRight: 12, flexShrink: 0 }}>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, -1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginRight: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                      disabled={values[rowIdx][colIdx] <= 0}
                    >-</button>
                    <span
                      style={{ width: 32, textAlign: 'center', fontSize: 20, color: '#222', fontWeight: 600, display: 'inline-block' }}
                    >{values[rowIdx][colIdx]}</span>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, 1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginLeft: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                    >+</button>
                  </div>
                ))}
                <div style={{ width: 100, textAlign: 'center', color: '#222', fontWeight: 700, fontSize: 18, boxSizing: 'border-box', flexShrink: 0 }}>
                  {values[rowIdx].reduce((a, b) => a + b, 0)}
                </div>
              </div>
            ))}
            {/* Suma rzutów total row */}
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid #bdbdbd' }}>
              <div style={{ width: 120, fontWeight: 700, color: '#222', fontSize: 18, flexShrink: 0 }}>{getTranslation('totalShots', language)}</div>
              {distances.map((dist, colIdx) => (
                <div key={dist} style={{ width: 120, textAlign: 'center', fontWeight: 700, color: '#2e4a21', fontSize: 18, marginRight: 12, flexShrink: 0 }}>
                  {values.reduce((sum, row) => sum + row[colIdx], 0)}
                </div>
              ))}
              <div style={{ width: 100, textAlign: 'center', fontWeight: 700, color: '#222', fontSize: 18, flexShrink: 0 }}>
                {values.reduce((sum, row) => sum + row.reduce((a, b) => a + b, 0), 0)}
              </div>
            </div>
          </div>
        </div>
      )}
      <div style={{ marginLeft: 40, marginTop: 32, display: 'flex', gap: 24 }}>
        <button
          onClick={handleSave}
          disabled={isSaved || saving || !isGridChanged}
          style={{
            padding: '12px 32px',
            borderRadius: 8,
            border: 'none',
            background: isSaved ? '#bdbdbd' : '#6dd5fa',
            color: 'white',
            fontWeight: 700,
            fontSize: 18,
            letterSpacing: 1,
            cursor: isSaved || !isGridChanged ? 'not-allowed' : 'pointer',
            transition: 'background 0.3s',
          }}
        >{getTranslation('saveTraining', language)}</button>
        {message && <span style={{ marginLeft: 24, color: isSaved ? '#2e7d32' : '#c62828', fontWeight: 600, fontSize: 16 }}>{message}</span>}
      </div>
    </div>
  );
}

function PuentaNiskaPage() {
  const { language } = useLanguage();
  
  // Helper function to translate database values
  const translateDatabaseValue = (value) => {
    if (!value) return value;
    
    // Debug: log the actual values from database
    console.log('Translating database value:', value, 'for language:', language);
    
    // Common terrain translations
    const terrainTranslations = {
      'pl': {
        'Trawa': 'Trawa',
        'trawa': 'Trawa',
        'Piasek': 'Piasek',
        'piasek': 'Piasek',
        'Ziemia': 'Ziemia',
        'ziemia': 'Ziemia',
        'Asfalt': 'Asfalt',
        'asfalt': 'Asfalt',
        'Beton': 'Beton',
        'beton': 'Beton',
        'Żwir': 'Żwir',
        'żwir': 'Żwir',
        'Miękko': 'Miękko',
        'miękko': 'Miękko',
        'Średnio': 'Średnio',
        'średnio': 'Średnio',
        'Twardo': 'Twardo',
        'twardo': 'Twardo'
      },
      'en': {
        'Trawa': 'Grass',
        'trawa': 'Grass',
        'Piasek': 'Sand',
        'piasek': 'Sand',
        'Ziemia': 'Dirt',
        'ziemia': 'Dirt',
        'Asfalt': 'Asphalt',
        'asfalt': 'Asphalt',
        'Beton': 'Concrete',
        'beton': 'Concrete',
        'Żwir': 'Gravel',
        'żwir': 'Gravel',
        'Miękko': 'Soft',
        'miękko': 'Soft',
        'Średnio': 'Medium',
        'średnio': 'Medium',
        'Twardo': 'Hard',
        'twardo': 'Hard'
      },
      'fr': {
        'Trawa': 'Herbe',
        'trawa': 'Herbe',
        'Piasek': 'Sable',
        'piasek': 'Sable',
        'Ziemia': 'Terre',
        'ziemia': 'Terre',
        'Asfalt': 'Asphalte',
        'asfalt': 'Asphalte',
        'Beton': 'Béton',
        'beton': 'Béton',
        'Żwir': 'Gravier',
        'żwir': 'Gravier',
        'Miękko': 'Mou',
        'miękko': 'Mou',
        'Średnio': 'Moyen',
        'średnio': 'Moyen',
        'Twardo': 'Dur',
        'twardo': 'Dur'
      }
    };
    
    // Common weather translations
    const weatherTranslations = {
      'pl': {
        'Słonecznie': 'Słonecznie',
        'słonecznie': 'Słonecznie',
        'Pochmurno': 'Pochmurno',
        'pochmurno': 'Pochmurno',
        'Deszcz': 'Deszcz',
        'deszcz': 'Deszcz',
        'deszczowo': 'Deszcz',
        'Deszczowo': 'Deszcz',
        'Wiatr': 'Wiatr',
        'wiatr': 'Wiatr',
        'Mgła': 'Mgła',
        'mgła': 'Mgła',
        'Śnieg': 'Śnieg',
        'śnieg': 'Śnieg'
      },
      'en': {
        'Słonecznie': 'Sunny',
        'słonecznie': 'Sunny',
        'Pochmurno': 'Cloudy',
        'pochmurno': 'Cloudy',
        'Deszcz': 'Wet',
        'deszcz': 'Wet',
        'deszczowo': 'Wet',
        'Deszczowo': 'Wet',
        'Wiatr': 'Windy',
        'wiatr': 'Windy',
        'Mgła': 'Foggy',
        'mgła': 'Foggy',
        'Śnieg': 'Snow',
        'śnieg': 'Snow'
      },
      'fr': {
        'Słonecznie': 'Ensoleillé',
        'słonecznie': 'Ensoleillé',
        'Pochmurno': 'Nuageux',
        'pochmurno': 'Nuageux',
        'Deszcz': 'Humide',
        'deszcz': 'Humide',
        'deszczowo': 'Humide',
        'Deszczowo': 'Humide',
        'Wiatr': 'Venteux',
        'wiatr': 'Venteux',
        'Mgła': 'Brumeux',
        'mgła': 'Brumeux',
        'Śnieg': 'Neigeux',
        'śnieg': 'Neigeux'
      }
    };
    
    // Try terrain translations first
    if (terrainTranslations[language] && terrainTranslations[language][value]) {
      return terrainTranslations[language][value];
    }
    
    // Try weather translations
    if (weatherTranslations[language] && weatherTranslations[language][value]) {
      return weatherTranslations[language][value];
    }
    
    // Return original value if no translation found
    return value;
  };
  
  const [terrainTypes, setTerrainTypes] = React.useState([]);
  const [weatherTypes, setWeatherTypes] = React.useState([]);
  const [selectedTerrain, setSelectedTerrain] = React.useState('');
  const [selectedWeather, setSelectedWeather] = React.useState('');
  const [temperature, setTemperature] = React.useState('');
  const [date, setDate] = React.useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = React.useState(() => new Date().toTimeString().slice(0,5));
  const distances = ['6,5 m', '7,5 m', '8,5 m', '9,5 m'];
  const columnKeys = ['<20 cm', '21-50 cm', '>50cm'];
  const [values, setValues] = React.useState(() => columnKeys.map(() => distances.map(() => 0)));
  const [isSaved, setIsSaved] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState('');

  // Helper to detect mobile
  const isMobile = window.innerWidth <= 600;

  React.useEffect(() => {
    fetch('/api/terrain-types')
      .then(res => res.json())
      .then(data => setTerrainTypes(data));
    fetch('/api/weather-types')
      .then(res => res.json())
      .then(data => setWeatherTypes(data));
  }, []);

  // Only enable save if any value in the 3x4 grid is nonzero
  const isGridChanged = values.flat().some(val => val > 0);

  React.useEffect(() => { setIsSaved(false); }, [values]);

  const handleValueChange = (rowIdx, colIdx, delta) => {
    setValues(prev => prev.map((row, r) =>
      r === rowIdx
        ? row.map((val, c) => c === colIdx ? Math.max(0, val + delta) : val)
        : row
    ));
  };

  const getDistanceNumber = (label) => parseFloat(label.replace(',', '.'));

  // Build training data for puenta niska: <20cm->hit, 21-50cm->touch, >50cm->missed
  const buildTrainingData = () => {
    const data = {};
    distances.forEach((dist, i) => {
      const distanceKey = dist.replace(',', '_').replace(' m', '');
      // For puenta: no carreau, so set to 0
      data[`carreau_${distanceKey}`] = 0;
      // Map: <20 cm -> hit, 21-50 cm -> touch, >50 cm -> missed
      data[`hit_${distanceKey}`] = values[0][i];      // <20 cm
      data[`touch_${distanceKey}`] = values[1][i];    // 21-50 cm
      data[`missed_${distanceKey}`] = values[2][i];   // >50 cm
    });
    return data;
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    if (!date) {
      setMessage('Data jest wymagana.');
      setSaving(false);
      return;
    }
    try {
      const token = sessionStorage.getItem('access_token');
      const res = await fetch('/api/trainings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          date,
          time,
          terrain: selectedTerrain || null,
          weather: selectedWeather || null,
          temperature: temperature === '' ? null : Number(temperature),
          type: 4,
          ...buildTrainingData(),
        }),
      });
      if (res.ok) {
        setIsSaved(true);
        setMessage('Trening zapisany!');
        // Reset all fields
        setSelectedTerrain('');
        setSelectedWeather('');
        setTemperature('');
        setDate(new Date().toISOString().slice(0, 10));
        setTime(new Date().toTimeString().slice(0,5));
        setValues(columnKeys.map(() => distances.map(() => 0)));
      } else {
        setMessage('Błąd zapisu treningu.');
      }
    } catch (e) {
      setMessage('Błąd połączenia z serwerem.');
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20 }}>
      {/* Language Selector - Top Right */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>

      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton />
        <Breadcrumbs />
      </div>
      <div className="petanka-form-row" style={{ display: 'flex', gap: 24, alignItems: 'center', marginLeft: 40, marginBottom: 32 }}>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('terrain', language)}:</label><br />
          <select value={selectedTerrain} onChange={e => setSelectedTerrain(Number(e.target.value))} style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 120 }}>
            <option value="">{getTranslation('selectOption', language)}</option>
            {terrainTypes.map((t) => <option key={t.terrain_index} value={t.terrain_index}>{translateDatabaseValue(t.terraintype)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('weather', language)}:</label><br />
          <select value={selectedWeather} onChange={e => setSelectedWeather(Number(e.target.value))} style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 120 }}>
            <option value="">{getTranslation('selectOption', language)}</option>
            {weatherTypes.map((w) => <option key={w.weather_index} value={w.weather_index}>{translateDatabaseValue(w.weathertype)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('temperature', language)}:</label><br />
          <input
            type="number"
            value={temperature}
            onChange={e => setTemperature(e.target.value)}
            min={-20}
            max={40}
            style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', width: 80 }}
          />
          <span style={{ marginLeft: 6, fontFamily: 'Nunito, Arial, sans-serif', color: '#888' }}>°C</span>
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('date', language)}:</label><br />
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            required
            style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 120 }}
          />
        </div>
        <div>
          <label style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, color: '#888' }}>{getTranslation('time', language)}:</label><br />
          <input
            type="time"
            value={time}
            onChange={e => setTime(e.target.value)}
            style={{ fontFamily: 'Nunito, Arial, sans-serif', fontSize: 16, padding: 6, borderRadius: 6, border: '1px solid #bdbdbd', minWidth: 100 }}
          />
        </div>
      </div>

      {/* Table or card layout depending on screen size */}
      {isMobile ? (
        <div style={{ marginLeft: 0, marginTop: 24, fontFamily: 'Nunito, Arial, sans-serif', width: '100%' }}>
          {distances.map((dist, colIdx) => (
            <div key={dist} style={{ background: '#f5f5f5', borderRadius: 16, margin: '0 12px 24px 12px', padding: 16, boxShadow: '0 2px 8px #e0eafc' }}>
              <div style={{ fontWeight: 700, color: '#222', fontSize: 18, marginBottom: 12, textAlign: 'center' }}>{dist}</div>
              {columnKeys.map((colKey, rowIdx) => (
                <div key={colKey} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ fontWeight: 600, color: '#888', fontSize: 16 }}>{getTranslation(colKey, language)}</span>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, -1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginRight: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                      disabled={values[rowIdx][colIdx] <= 0}
                    >-</button>
                    <span style={{ width: 32, textAlign: 'center', fontSize: 20, color: '#222', fontWeight: 600, display: 'inline-block' }}>{values[rowIdx][colIdx]}</span>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, 1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginLeft: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                    >+</button>
                  </div>
                </div>
              ))}
              <div style={{ borderTop: '1px solid #bdbdbd', marginTop: 12, paddingTop: 10, textAlign: 'right', fontWeight: 700, color: '#2e4a21', fontSize: 18 }}>
                {getTranslation('totalShots', language)}: {values.reduce((sum, row) => sum + row[colIdx], 0)}
              </div>
            </div>
          ))}
          <div style={{ textAlign: 'center', fontWeight: 700, color: '#222', fontSize: 18, margin: '12px 0 0 0' }}>
            {getTranslation('totalAllShots', language)}: {values.reduce((sum, row) => sum + row.reduce((a, b) => a + b, 0), 0)}
          </div>
        </div>
      ) : (
        <div style={{ marginLeft: 40, marginTop: 24, fontFamily: 'Nunito, Arial, sans-serif' }}>
          <div style={{ 
            background: '#f5f5f5', 
            borderRadius: 16, 
            padding: 24, 
            boxShadow: '0 2px 8px #e0eafc', 
            marginRight: 40,
            overflowX: 'auto',
            display: 'inline-block'
          }}>
            <div style={{ 
              display: 'flex', 
              fontWeight: 700, 
              color: '#888', 
              fontSize: 18, 
              marginBottom: 16
            }}>
              <div style={{ width: 120, textAlign: 'left', boxSizing: 'border-box', flexShrink: 0 }}>{getTranslation('distance', language)}</div>
              {distances.map((dist, idx) => (
                <div key={dist} style={{ width: 120, textAlign: 'center', boxSizing: 'border-box', marginRight: 12, flexShrink: 0 }}>{dist}</div>
              ))}
              <div style={{ width: 100, textAlign: 'center', color: '#222', boxSizing: 'border-box', flexShrink: 0 }}>{getTranslation('totalShots', language)}</div>
            </div>
            {columnKeys.map((colKey, rowIdx) => (
              <div key={colKey} style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ width: 120, color: '#222', fontWeight: 600, textAlign: 'left', boxSizing: 'border-box', flexShrink: 0 }}>{getTranslation(colKey, language)}</div>
                {distances.map((dist, colIdx) => (
                  <div key={dist} style={{ width: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', marginRight: 12, flexShrink: 0 }}>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, -1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginRight: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                      disabled={values[rowIdx][colIdx] <= 0}
                    >-</button>
                    <span
                      style={{ width: 32, textAlign: 'center', fontSize: 20, color: '#222', fontWeight: 600, display: 'inline-block' }}
                    >{values[rowIdx][colIdx]}</span>
                    <button
                      onClick={() => handleValueChange(rowIdx, colIdx, 1)}
                      style={{ fontSize: 28, width: 40, height: 40, borderRadius: 8, border: '1px solid #bdbdbd', background: '#fff', color: '#888', marginLeft: 8, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                    >+</button>
                  </div>
                ))}
                <div style={{ width: 100, textAlign: 'center', color: '#222', fontWeight: 700, fontSize: 18, boxSizing: 'border-box', flexShrink: 0 }}>
                  {values[rowIdx].reduce((a, b) => a + b, 0)}
                </div>
              </div>
            ))}
            {/* Suma rzutów total row */}
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid #bdbdbd' }}>
              <div style={{ width: 120, fontWeight: 700, color: '#222', fontSize: 18, flexShrink: 0 }}>{getTranslation('totalShots', language)}</div>
              {distances.map((dist, colIdx) => (
                <div key={dist} style={{ width: 120, textAlign: 'center', fontWeight: 700, color: '#2e4a21', fontSize: 18, marginRight: 12, flexShrink: 0 }}>
                  {values.reduce((sum, row) => sum + row[colIdx], 0)}
                </div>
              ))}
              <div style={{ width: 100, textAlign: 'center', fontWeight: 700, color: '#222', fontSize: 18, flexShrink: 0 }}>
                {values.reduce((sum, row) => sum + row.reduce((a, b) => a + b, 0), 0)}
              </div>
            </div>
          </div>
        </div>
      )}
      <div style={{ marginLeft: 40, marginTop: 32, display: 'flex', gap: 24 }}>
        <button
          onClick={handleSave}
          disabled={isSaved || saving || !isGridChanged}
          style={{
            padding: '12px 32px',
            borderRadius: 8,
            border: 'none',
            background: isSaved ? '#bdbdbd' : '#6dd5fa',
            color: 'white',
            fontWeight: 700,
            fontSize: 18,
            letterSpacing: 1,
            cursor: isSaved || !isGridChanged ? 'not-allowed' : 'pointer',
            transition: 'background 0.3s',
          }}
        >{getTranslation('saveTraining', language)}</button>
        {message && <span style={{ marginLeft: 24, color: isSaved ? '#2e7d32' : '#c62828', fontWeight: 600, fontSize: 16 }}>{message}</span>}
      </div>
    </div>
  );
}

function PrzegladTreningowPage() {
  const { language } = useLanguage();
  const [trainings, setTrainings] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [calendarDate, setCalendarDate] = React.useState(new Date());
  const [selectedDay, setSelectedDay] = React.useState(null);
  const [dayTrainings, setDayTrainings] = React.useState([]);
  const [editingType, setEditingType] = React.useState(null);
  const [editValues, setEditValues] = React.useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [deleteType, setDeleteType] = React.useState(null);
  const [message, setMessage] = React.useState('');

  // Training type definitions
  const trainingTypes = {
    1: { nameKey: 'shotAtBall', shotTypes: [
      { key: 'carreau', labelKey: 'carreau' },
      { key: 'hit', labelKey: 'hit' },
      { key: 'touch', labelKey: 'touch' },
      { key: 'missed', labelKey: 'missed' },
    ]},
    2: { nameKey: 'shotAtPig', shotTypes: [
      { key: 'hit', labelKey: 'hit' },
      { key: 'touch', labelKey: 'touch' },
      { key: 'missed', labelKey: 'missed' },
    ]},
    3: { nameKey: 'highPoint', shotTypes: [
      { key: 'hit', labelKey: '<20 cm' },
      { key: 'touch', labelKey: '21-50 cm' },
      { key: 'missed', labelKey: '>50cm' },
    ]},
    4: { nameKey: 'lowPoint', shotTypes: [
      { key: 'hit', labelKey: '<20 cm' },
      { key: 'touch', labelKey: '21-50 cm' },
      { key: 'missed', labelKey: '>50cm' },
    ]},
  };

  // Fetch all trainings for the logged-in user ONCE
  React.useEffect(() => {
    const fetchAllTrainings = async () => {
      setLoading(true);
      setError('');
      setTrainings([]);
      setSelectedDay(null);
      setDayTrainings([]);
      try {
        const token = sessionStorage.getItem('access_token');
        const response = await fetch('/api/trainings', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          console.log('Fetched trainings:', data.map(t => ({ id: t.id, type: t.type, date: t.date, time: t.time })));
          setTrainings(data);
        } else {
          setError('Błąd pobierania treningów');
        }
      } catch (err) {
        setError('Błąd połączenia z serwerem');
      } finally {
        setLoading(false);
      }
    };
    fetchAllTrainings();
  }, []);

  // Extract all unique training dates as 'yyyy-MM-dd'
  const trainingDays = React.useMemo(() => {
    const days = Array.from(new Set(trainings.map(t => {
      // Handle both string and Date object formats
      if (typeof t.date === 'string') {
        return t.date.slice(0, 10);
      } else if (t.date instanceof Date) {
        return t.date.toISOString().slice(0, 10);
      } else {
        // If it's already a date string in YYYY-MM-DD format
        return t.date;
      }
    })));
    console.log('Training days extracted:', days);
    console.log('Raw trainings data:', trainings.map(t => ({ id: t.id, date: t.date, dateType: typeof t.date })));
    return days;
  }, [trainings]);

  // When a day is clicked, show details for that day (compare as local date string)
  const handleDayClick = (date) => {
    const dayStr = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
    const filtered = trainings.filter(t => {
      // Handle both string and Date object formats
      let trainingDateStr;
      if (typeof t.date === 'string') {
        trainingDateStr = t.date.slice(0, 10);
      } else if (t.date instanceof Date) {
        trainingDateStr = t.date.toISOString().slice(0, 10);
      } else {
        trainingDateStr = t.date;
      }
      return trainingDateStr === dayStr;
    });
    console.log('Selected day:', dayStr);
    console.log('Filtered trainings for this day:', filtered.map(t => ({ id: t.id, type: t.type, time: t.time })));
    setSelectedDay(dayStr);
    setDayTrainings(filtered);
  };

  const distances = ['6_5', '7_5', '8_5', '9_5'];
  const distanceLabels = ['6,5 m', '7,5 m', '8,5 m', '9,5 m'];

  // Helper function to calculate table data for a single training record
  const calculateSingleTrainingData = (training, shotTypes) => {
    const sums = {};
    let totalShots = 0;
    shotTypes.forEach(type => {
      sums[type.key] = {};
      distances.forEach(dist => {
        sums[type.key][dist] = training[`${type.key}_${dist}`] || 0;
      });
    });
    const sumPerDist = distances.map(dist => {
      let sum = 0;
      shotTypes.forEach(type => { sum += sums[type.key][dist]; });
      totalShots += sum;
      return sum;
    });
    const sumPerType = shotTypes.map(type => distances.reduce((acc, dist) => acc + sums[type.key][dist], 0));
    const percent = (val, total) => total ? ((val / total) * 100).toFixed(2) + '%' : '0.00%';
    return { sums, sumPerDist, sumPerType, totalShots, percent };
  };

  // Handle edit mode toggle
  const handleEditToggle = (trainingId, tableData) => {
    if (editingType === trainingId) {
      setEditingType(null);
      setEditValues({});
    } else {
      setEditingType(trainingId);
      // Initialize edit values with current table data
      const initialValues = {};
      const training = dayTrainings.find(t => t.id === trainingId);
      if (training) {
        const typeData = trainingTypes[training.type];
        typeData.shotTypes.forEach(type => {
          distances.forEach(dist => {
            initialValues[`${type.key}_${dist}`] = tableData.sums[type.key][dist];
          });
        });
      }
      setEditValues(initialValues);
    }
  };

  // Handle edit value change
  const handleEditValueChange = (typeKey, distance, value) => {
    setEditValues(prev => ({
      ...prev,
      [`${typeKey}_${distance}`]: Math.max(0, parseInt(value) || 0)
    }));
  };

  // Handle save edited training
  const handleSaveEdit = async (trainingId) => {
    setLoading(true);
    setMessage('');
    try {
      console.log('handleSaveEdit called with trainingId:', trainingId, 'type:', typeof trainingId);
      const token = sessionStorage.getItem('access_token');
      console.log('Token:', token ? 'present' : 'missing');
      
      console.log('dayTrainings:', dayTrainings.map(t => ({ id: t.id, type: typeof t.id, type: t.type, time: t.time })));
      
      const training = dayTrainings.find(t => t.id === parseInt(trainingId));
      console.log('Found training:', training);
      
      if (!training) {
        throw new Error('Nie znaleziono treningu do aktualizacji');
      }

      const updateData = {};
      const typeData = trainingTypes[training.type];
      typeData.shotTypes.forEach(type => {
        distances.forEach(dist => {
          updateData[`${type.key}_${dist}`] = editValues[`${type.key}_${dist}`] || 0;
        });
      });

      console.log('Updating training:', training.id, 'with data:', updateData);
      console.log('API URL:', `/api/trainings/${training.id}`);

      const response = await fetch(`/api/trainings/${training.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server response:', response.status, errorText);
        throw new Error(`Błąd aktualizacji treningu: ${response.status} - ${errorText}`);
      }

      setMessage('Trening został zaktualizowany!');
      setEditingType(null);
      setEditValues({});
      
      // Refresh trainings data
      const refreshResponse = await fetch('/api/trainings', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (refreshResponse.ok) {
        const data = await refreshResponse.json();
        setTrainings(data);
        const dayStr = selectedDay;
        const filtered = data.filter(t => t.date.slice(0, 10) === dayStr);
        setDayTrainings(filtered);
      }
    } catch (err) {
      console.error('Error in handleSaveEdit:', err);
      setMessage('Błąd aktualizacji treningu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete training
  const handleDeleteTraining = async () => {
    setLoading(true);
    setMessage('');
    try {
      const token = sessionStorage.getItem('access_token');
      const training = dayTrainings.find(t => t.id === parseInt(deleteType));
      
      if (!training) {
        throw new Error('Nie znaleziono treningu do usunięcia');
      }

      const response = await fetch(`/api/trainings/${training.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Błąd usuwania treningu');
      }

      setMessage('Trening został usunięty!');
      setShowDeleteConfirm(false);
      setDeleteType(null);
      
      // Refresh trainings data
      const refreshResponse = await fetch('/api/trainings', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (refreshResponse.ok) {
        const data = await refreshResponse.json();
        setTrainings(data);
        const dayStr = selectedDay;
        const filtered = data.filter(t => t.date.slice(0, 10) === dayStr);
        setDayTrainings(filtered);
      }
    } catch (err) {
      setMessage('Błąd usuwania treningu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Render table for a single training record
  const renderTrainingTable = (training) => {
    const typeData = trainingTypes[training.type];
    const tableData = calculateSingleTrainingData(training, typeData.shotTypes);
    const isEditing = editingType === training.id;
    
    return (
      <div key={training.id} style={{ marginBottom: 32 }}>
        <h3 style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 700, marginBottom: 16, color: '#2e4a21' }}>
          {getTranslation(typeData.nameKey, language)} - {training.time}
        </h3>
        <div style={{ 
          overflowX: 'auto', 
          width: '100%',
          maxWidth: '100%',
          WebkitOverflowScrolling: 'touch',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          position: 'relative',
          boxSizing: 'border-box'
        }}>
          <table style={{ 
            borderCollapse: 'separate', 
            borderSpacing: 0,
            minWidth: 700, 
            width: '100%',
            fontFamily: 'Nunito, Arial, sans-serif', 
            fontSize: window.innerWidth <= 600 ? 13 : 15 
          }}>
            <thead>
              <tr>
                <th style={{ 
                  background: '#ffe066', 
                  border: '1px solid #bdbdbd', 
                  padding: window.innerWidth <= 600 ? 8 : 6, 
                  fontSize: window.innerWidth <= 600 ? 12 : 15,
                  position: 'sticky',
                  left: 0,
                  zIndex: 10,
                  minWidth: 80
                }}>{getTranslation('distance', language)}</th>
                {distanceLabels.map((label, i) => (
                  <React.Fragment key={label}>
                    <th style={{ background: '#ffe066', border: '1px solid #bdbdbd', padding: window.innerWidth <= 600 ? 8 : 6, fontSize: window.innerWidth <= 600 ? 12 : 15 }}>{label}</th>
                    {!isEditing && <th style={{ background: '#ffe066', border: '1px solid #bdbdbd', padding: window.innerWidth <= 600 ? 8 : 6, fontSize: window.innerWidth <= 600 ? 12 : 15 }}>%</th>}
                  </React.Fragment>
                ))}
                <th style={{ background: '#b5e0fa', border: '1px solid #bdbdbd', padding: window.innerWidth <= 600 ? 8 : 6, fontSize: window.innerWidth <= 600 ? 12 : 15 }}>{getTranslation('totalShots', language)}</th>
                {!isEditing && <th style={{ background: '#b5e0fa', border: '1px solid #bdbdbd', padding: window.innerWidth <= 600 ? 8 : 6, fontSize: window.innerWidth <= 600 ? 12 : 15 }}>%</th>}
              </tr>
            </thead>
            <tbody>
              {typeData.shotTypes.map((type, rowIdx) => {
                const rowSum = distances.reduce((sum, dist) => {
                  const value = isEditing ? (editValues[`${type.key}_${dist}`] || 0) : tableData.sums[type.key][dist];
                  return sum + value;
                }, 0);
                
                return (
                  <tr key={type.key}>
                    <td style={{ 
                      border: '1px solid #bdbdbd', 
                      padding: window.innerWidth <= 600 ? 8 : 6, 
                      fontSize: window.innerWidth <= 600 ? 12 : 15,
                      position: 'sticky',
                      left: 0,
                      zIndex: 5,
                      background: 'white',
                      minWidth: 80
                    }}>{getTranslation(type.labelKey, language)}</td>
                    {distances.map((dist, i) => {
                      const value = isEditing ? (editValues[`${type.key}_${dist}`] || 0) : tableData.sums[type.key][dist];
                      const distSum = typeData.shotTypes.reduce((sum, shotType) => {
                        const shotValue = isEditing ? (editValues[`${shotType.key}_${dist}`] || 0) : tableData.sums[shotType.key][dist];
                        return sum + shotValue;
                      }, 0);
                      
                      return (
                        <React.Fragment key={dist}>
                          <td style={{ border: '1px solid #bdbdbd', padding: window.innerWidth <= 600 ? 8 : 6, fontSize: window.innerWidth <= 600 ? 12 : 15 }}>
                            {isEditing ? (
                              <input
                                type="number"
                                min="0"
                                value={value}
                                onChange={(e) => handleEditValueChange(type.key, dist, e.target.value)}
                                style={{
                                  width: '100%',
                                  border: 'none',
                                  background: 'transparent',
                                  textAlign: 'center',
                                  fontSize: window.innerWidth <= 600 ? 12 : 15,
                                  fontFamily: 'Nunito, Arial, sans-serif'
                                }}
                              />
                            ) : (
                              value
                            )}
                          </td>
                          {!isEditing && (
                            <td style={{ border: '1px solid #bdbdbd', padding: window.innerWidth <= 600 ? 8 : 6, fontSize: window.innerWidth <= 600 ? 12 : 15 }}>
                              {tableData.percent(value, distSum)}
                            </td>
                          )}
                        </React.Fragment>
                      );
                    })}
                    <td style={{ border: '1px solid #bdbdbd', padding: window.innerWidth <= 600 ? 8 : 6, background: '#e3f2fd', fontSize: window.innerWidth <= 600 ? 12 : 15 }}>{rowSum}</td>
                    {!isEditing && (
                      <td style={{ border: '1px solid #bdbdbd', padding: window.innerWidth <= 600 ? 8 : 6, background: '#e3f2fd', fontSize: window.innerWidth <= 600 ? 12 : 15 }}>
                        {tableData.percent(rowSum, tableData.totalShots)}
                      </td>
                    )}
                  </tr>
                );
              })}
              <tr style={{ background: '#e3f2fd', fontWeight: 700 }}>
                <td style={{ 
                  fontSize: window.innerWidth <= 600 ? 12 : 15, 
                  padding: window.innerWidth <= 600 ? 8 : 6,
                  position: 'sticky',
                  left: 0,
                  zIndex: 5,
                  background: '#e3f2fd',
                  minWidth: 80,
                  border: '1px solid #bdbdbd'
                }}>{getTranslation('totalShots', language)}</td>
                {distances.map((dist, i) => {
                  const distSum = typeData.shotTypes.reduce((sum, shotType) => {
                    const value = isEditing ? (editValues[`${shotType.key}_${dist}`] || 0) : tableData.sums[shotType.key][dist];
                    return sum + value;
                  }, 0);
                  
                  return (
                    <React.Fragment key={dist}>
                      <td style={{ border: '1px solid #bdbdbd', padding: window.innerWidth <= 600 ? 8 : 6, fontSize: window.innerWidth <= 600 ? 12 : 15 }}>{distSum}</td>
                      {!isEditing && (
                        <td style={{ border: '1px solid #bdbdbd', padding: window.innerWidth <= 600 ? 8 : 6, fontSize: window.innerWidth <= 600 ? 12 : 15 }}>
                          {tableData.percent(distSum, tableData.totalShots)}
                        </td>
                      )}
                    </React.Fragment>
                  );
                })}
                <td style={{ border: '1px solid #bdbdbd', padding: window.innerWidth <= 600 ? 8 : 6, background: '#90caf9', fontSize: window.innerWidth <= 600 ? 12 : 15 }}>
                  {typeData.shotTypes.reduce((total, shotType) => {
                    return total + distances.reduce((sum, dist) => {
                      const value = isEditing ? (editValues[`${shotType.key}_${dist}`] || 0) : tableData.sums[shotType.key][dist];
                      return sum + value;
                    }, 0);
                  }, 0)}
                </td>
                {!isEditing && (
                  <td style={{ border: '1px solid #bdbdbd', padding: window.innerWidth <= 600 ? 8 : 6, background: '#90caf9', fontSize: window.innerWidth <= 600 ? 12 : 15 }}>100.00%</td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Action buttons */}
        <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
          {isEditing ? (
            <button
              onClick={() => handleSaveEdit(training.id)}
              disabled={loading}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                background: '#4caf50',
                color: 'white',
                fontWeight: 600,
                fontSize: 14,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background 0.3s',
              }}
            >
              {getTranslation('save', language)}
            </button>
          ) : (
            <button
              onClick={() => handleEditToggle(training.id, tableData)}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                background: '#2196f3',
                color: 'white',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                transition: 'background 0.3s',
              }}
            >
              {getTranslation('edit', language)}
            </button>
          )}
          
          <button
            onClick={() => {
              setDeleteType(training.id);
              setShowDeleteConfirm(true);
            }}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: 'none',
              background: '#f44336',
              color: 'white',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              transition: 'background 0.3s',
            }}
          >
            {getTranslation('delete', language)}
          </button>
        </div>
      </div>
    );
  };

  // Custom renderDay for DateCalendar
  const renderDay = (props) => {
    const { day, ...pickersDayProps } = props;
    
    // Ensure day is a Date object
    if (!day || !(day instanceof Date)) {
      return <PickersDay {...props} />;
    }
    
    const dayStr = day.getFullYear() + '-' + String(day.getMonth() + 1).padStart(2, '0') + '-' + String(day.getDate()).padStart(2, '0');
    const hasTraining = trainingDays.includes(dayStr);
    
    // Debug logging for specific dates
    if (dayStr === '2025-08-02' || dayStr === '2025-07-29') {
      console.log(`Day ${dayStr}: hasTraining = ${hasTraining}, trainingDays =`, trainingDays);
    }
    
    return (
      <div style={{ position: 'relative' }}>
        <PickersDay day={day} {...pickersDayProps} />
        {hasTraining && pickersDayProps.outsideCurrentMonth === false && (
          <span
            style={{
              position: 'absolute',
              top: 4,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: '1px solid #000000',
              backgroundColor: 'transparent',
              boxSizing: 'border-box',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div style={{ 
      position: 'relative', 
      minHeight: '100vh', 
      padding: 20,
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      boxSizing: 'border-box'
    }}>
      {/* Language Selector - Top Right */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>

      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton />
        <Breadcrumbs />
      </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginTop: 40, paddingLeft: 20 }}>
        <div style={{ maxWidth: 400, marginBottom: 32, background: 'white', padding: 20, borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={language === 'pl' ? pl : language === 'fr' ? fr : enUS}>
            <DateCalendar
              value={calendarDate}
              onChange={date => { setCalendarDate(date); setSelectedDay(null); setDayTrainings([]); handleDayClick(date); }}
              slots={{
                day: renderDay
              }}
            />
          </LocalizationProvider>
        </div>
        {loading && <div style={{ color: '#888', fontSize: 16, textAlign: 'left' }}>Ładowanie...</div>}
        {error && <div style={{ color: '#c62828', fontWeight: 600, marginTop: 20, textAlign: 'left' }}>{error}</div>}
        {message && (
          <div style={{ 
            color: message.includes('zaktualizowany') || message.includes('usunięty') ? '#2e7d32' : '#c62828', 
            fontWeight: 600, 
            marginTop: 20,
            padding: '8px 12px',
            borderRadius: 4,
            background: message.includes('zaktualizowany') || message.includes('usunięty') ? '#e8f5e8' : '#ffebee',
            textAlign: 'left'
          }}>
            {message}
          </div>
        )}
        {selectedDay && dayTrainings.length > 0 && (
          <div style={{ 
            marginTop: 32, 
            width: '100%', 
            maxWidth: '100%', 
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}>
            <h2 style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 700, marginBottom: 16, textAlign: 'left' }}>{getTranslation('trainingDetails', language)}: {selectedDay}</h2>
            {dayTrainings
              .sort((a, b) => a.time.localeCompare(b.time))
              .map(training => renderTrainingTable(training))}
          </div>
        )}
        {!loading && !selectedDay && (
          <div style={{ marginTop: 32, color: '#888', fontSize: 16, textAlign: 'left' }}>
            {getTranslation('clickDayForDetails', language)}
          </div>
        )}
        {!loading && selectedDay && dayTrainings.length === 0 && (
          <div style={{ marginTop: 32, color: '#888', fontSize: 16, textAlign: 'left' }}>
            {getTranslation('noTrainingsDay', language)}
          </div>
        )}
      </div>

      {/* Delete confirmation popup */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'white',
            padding: 32,
            borderRadius: 12,
            maxWidth: 400,
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}>
            <h3 style={{ 
              fontFamily: 'Nunito, Arial, sans-serif', 
              fontWeight: 700, 
              marginBottom: 16,
              color: '#c62828'
            }}>
              Jesteś pewien, że chcesz usunąć trening?
            </h3>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24 }}>
              <button
                onClick={handleDeleteTraining}
                disabled={loading}
                style={{
                  padding: '10px 24px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#f44336',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'background 0.3s',
                }}
              >
                Tak
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteType(null);
                }}
                style={{
                  padding: '10px 24px',
                  borderRadius: 6,
                  border: '1px solid #bdbdbd',
                  background: 'white',
                  color: '#666',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'background 0.3s',
                }}
              >
                Nie
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Add the MeczTowarzyskiPage component before the closing of the file
function MeczTowarzyskiPage({ userProfile }) {
  const { language } = useLanguage();
  // State for team sizes
  const [yourTeamSize, setYourTeamSize] = React.useState('singiel');
  const [opponentTeamSize, setOpponentTeamSize] = React.useState('singiel');
  
  // State for team players - initialize with logged user's name
  const [yourTeam, setYourTeam] = React.useState({ 
    players: ['', '', '']
  });

  // Update the first player when userProfile changes
  React.useEffect(() => {
    if (userProfile) {
      setYourTeam(prev => ({
        ...prev,
        players: [
          `${userProfile.firstname} ${userProfile.lastname}`.trim(),
          prev.players[1],
          prev.players[2]
        ]
      }));
    }
  }, [userProfile]);
  const [opponentTeam, setOpponentTeam] = React.useState({ players: ['', '', ''] });
  
  // Handlers for team changes
  const handleYourTeamChange = (playerIndex, value) => {
    setYourTeam(prev => ({
      ...prev,
      players: prev.players.map((player, index) => 
        index === playerIndex ? value : player
      )
    }));
  };
  
  const handleOpponentTeamChange = (playerIndex, value) => {
    setOpponentTeam(prev => ({
      ...prev,
      players: prev.players.map((player, index) => 
        index === playerIndex ? value : player
      )
    }));
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20 }}>
      {/* Language Selector - Top Right */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>

      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton />
        <Breadcrumbs />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        
        {/* Note */}
        <div style={{ marginBottom: 20 }}>
          <p style={{ 
            fontFamily: 'Nunito, Arial, sans-serif', 
            fontSize: 18, 
            color: '#666', 
            margin: 0,
            fontWeight: 500,
            textAlign: 'center'
          }}>
            Podaj składy drużyn:
          </p>
        </div>
        
        {/* Team Selection Interface */}
        <div style={{ 
          display: 'flex', 
          flexDirection: window.innerWidth <= 600 ? 'column' : 'row', 
          gap: 24, 
          justifyContent: 'center',
          alignItems: window.innerWidth <= 600 ? 'stretch' : 'flex-start',
          maxWidth: '800px',
          width: '100%'
        }}>
        {/* Twoja Drużyna Section */}
        <div style={{ 
          background: '#e3f2fd', 
          padding: 24, 
          borderRadius: 16, 
          boxShadow: '0 4px 24px rgba(31,38,135,0.08)'
        }}>
          <h2 style={{ 
            margin: '0 0 20px 0', 
            fontFamily: 'Nunito, Arial, sans-serif', 
            fontWeight: 700, 
            color: '#1976d2',
            fontSize: 20
          }}>
            twoja drużyna
          </h2>
          
          {/* Team Size Selection */}
                            <div style={{ marginBottom: 24 }}>
                    <div style={{ marginBottom: 12, fontWeight: 600, color: '#333' }}>ilość graczy:</div>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                      {[
                        { value: 'singiel', label: '1' },
                        { value: 'dublet', label: '2' },
                        { value: 'triplet', label: '3' }
                      ].map(({ value, label }) => (
                        <button
                          key={value}
                          onClick={() => setYourTeamSize(value)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: 6,
                            border: yourTeamSize === value ? '2px solid #1976d2' : '1px solid #bdbdbd',
                            background: yourTeamSize === value ? '#1976d2' : 'white',
                            color: yourTeamSize === value ? 'white' : '#333',
                            fontWeight: 600,
                            fontSize: 14,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            width: 'fit-content',
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
          
          {/* Player Input Fields */}
          <div>
            {yourTeamSize === 'singiel' && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', marginBottom: 4, color: '#666' }}>gracz 1:</label>
                <input
                  type="text"
                  value={yourTeam.players[0] || ''}
                  readOnly
                  style={{
                    width: '100%',
                    minWidth: window.innerWidth <= 600 ? '100%' : '280px',
                    padding: '8px 12px',
                    border: '1px solid #bdbdbd',
                    borderRadius: 6,
                    fontSize: 14,
                    fontFamily: 'Nunito, Arial, sans-serif',
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed'
                  }}
                  placeholder="imię i nazwisko"
                />
              </div>
            )}
            {yourTeamSize === 'dublet' && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#666' }}>gracz 1:</label>
                  <input
                    type="text"
                    value={yourTeam.players[0] || ''}
                    readOnly
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #bdbdbd',
                      borderRadius: 6,
                      fontSize: 14,
                      fontFamily: 'Nunito, Arial, sans-serif',
                      backgroundColor: '#f5f5f5',
                      cursor: 'not-allowed'
                    }}
                    placeholder="imię i nazwisko"
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#666' }}>gracz 2:</label>
                  <input
                    type="text"
                    value={yourTeam.players[1] || ''}
                    onChange={(e) => handleYourTeamChange(1, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #bdbdbd',
                      borderRadius: 6,
                      fontSize: 14,
                      fontFamily: 'Nunito, Arial, sans-serif'
                    }}
                    placeholder="imię i nazwisko"
                  />
                </div>
              </>
            )}
            {yourTeamSize === 'triplet' && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#666' }}>gracz 1:</label>
                  <input
                    type="text"
                    value={yourTeam.players[0] || ''}
                    readOnly
                    style={{
                      width: '100%',
                      minWidth: '280px',
                      padding: '8px 12px',
                      border: '1px solid #bdbdbd',
                      borderRadius: 6,
                      fontSize: 14,
                      fontFamily: 'Nunito, Arial, sans-serif',
                      backgroundColor: '#f5f5f5',
                      cursor: 'not-allowed'
                    }}
                    placeholder="imię i nazwisko"
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#666' }}>gracz 2:</label>
                  <input
                    type="text"
                    value={yourTeam.players[1] || ''}
                    onChange={(e) => handleYourTeamChange(1, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #bdbdbd',
                      borderRadius: 6,
                      fontSize: 14,
                      fontFamily: 'Nunito, Arial, sans-serif'
                    }}
                    placeholder="imię i nazwisko"
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#666' }}>gracz 3:</label>
                  <input
                    type="text"
                    value={yourTeam.players[2] || ''}
                    onChange={(e) => handleYourTeamChange(2, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #bdbdbd',
                      borderRadius: 6,
                      fontSize: 14,
                      fontFamily: 'Nunito, Arial, sans-serif'
                    }}
                    placeholder="imię i nazwisko"
                  />
                </div>
              </>
            )}
          </div>
        </div>
        
        {/* Przeciwnicy Section */}
        <div style={{ 
          background: '#ffebee', 
          padding: 24, 
          borderRadius: 16, 
          boxShadow: '0 4px 24px rgba(31,38,135,0.08)'
        }}>
          <h2 style={{ 
            margin: '0 0 20px 0', 
            fontFamily: 'Nunito, Arial, sans-serif', 
            fontWeight: 700, 
            color: '#d32f2f',
            fontSize: 20
          }}>
            przeciwnicy
          </h2>
          
          {/* Team Size Selection */}
                            <div style={{ marginBottom: 24 }}>
                    <div style={{ marginBottom: 12, fontWeight: 600, color: '#333' }}>ilość graczy:</div>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                      {[
                        { value: 'singiel', label: '1' },
                        { value: 'dublet', label: '2' },
                        { value: 'triplet', label: '3' }
                      ].map(({ value, label }) => (
                        <button
                          key={value}
                          onClick={() => setOpponentTeamSize(value)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: 6,
                            border: opponentTeamSize === value ? '2px solid #d32f2f' : '1px solid #bdbdbd',
                            background: opponentTeamSize === value ? '#d32f2f' : 'white',
                            color: opponentTeamSize === value ? 'white' : '#333',
                            fontWeight: 600,
                            fontSize: 14,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            width: 'fit-content',
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
          
          {/* Player Input Fields */}
          <div>
            {opponentTeamSize === 'singiel' && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', marginBottom: 4, color: '#666' }}>gracz 1:</label>
                <input
                  type="text"
                  value={opponentTeam.players[0] || ''}
                  onChange={(e) => handleOpponentTeamChange(0, e.target.value)}
                  style={{
                    width: '100%',
                    minWidth: window.innerWidth <= 600 ? '100%' : '280px',
                    padding: '8px 12px',
                    border: '1px solid #bdbdbd',
                    borderRadius: 6,
                    fontSize: 14,
                    fontFamily: 'Nunito, Arial, sans-serif'
                  }}
                  placeholder="imię i nazwisko"
                />
              </div>
            )}
            {opponentTeamSize === 'dublet' && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#666' }}>gracz 1:</label>
                  <input
                    type="text"
                    value={opponentTeam.players[0] || ''}
                    onChange={(e) => handleOpponentTeamChange(0, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #bdbdbd',
                      borderRadius: 6,
                      fontSize: 14,
                      fontFamily: 'Nunito, Arial, sans-serif'
                    }}
                    placeholder="imię i nazwisko"
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#666' }}>gracz 2:</label>
                  <input
                    type="text"
                    value={opponentTeam.players[1] || ''}
                    onChange={(e) => handleOpponentTeamChange(1, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #bdbdbd',
                      borderRadius: 6,
                      fontSize: 14,
                      fontFamily: 'Nunito, Arial, sans-serif'
                    }}
                    placeholder="imię i nazwisko"
                  />
                </div>
              </>
            )}
            {opponentTeamSize === 'triplet' && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#666' }}>gracz 1:</label>
                  <input
                    type="text"
                    value={opponentTeam.players[0] || ''}
                    onChange={(e) => handleOpponentTeamChange(0, e.target.value)}
                    style={{
                      width: '100%',
                      minWidth: window.innerWidth <= 600 ? '100%' : '280px',
                      padding: '8px 12px',
                      border: '1px solid #bdbdbd',
                      borderRadius: 6,
                      fontSize: 14,
                      fontFamily: 'Nunito, Arial, sans-serif'
                    }}
                    placeholder="imię i nazwisko"
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#666' }}>gracz 2:</label>
                  <input
                    type="text"
                    value={opponentTeam.players[1] || ''}
                    onChange={(e) => handleOpponentTeamChange(1, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #bdbdbd',
                      borderRadius: 6,
                      fontSize: 14,
                      fontFamily: 'Nunito, Arial, sans-serif'
                    }}
                    placeholder="imię i nazwisko"
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#666' }}>gracz 3:</label>
                  <input
                    type="text"
                    value={opponentTeam.players[2] || ''}
                    onChange={(e) => handleOpponentTeamChange(2, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #bdbdbd',
                      borderRadius: 6,
                      fontSize: 14,
                      fontFamily: 'Nunito, Arial, sans-serif'
                    }}
                    placeholder="imię i nazwisko"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
  );
}

// Main App component that provides the Router and Language Context
function App() {
  return (
    <LanguageProvider>
      <Router>
        <AppContent />
      </Router>
    </LanguageProvider>
  );
}

export default App;
